'use client';

import React, { useEffect, useRef } from 'react';
import { ControlMode } from './RunSceneShell';
import { GameState, HUDData, OutfitData } from '../types/game';
import { pushGameLog } from './GameLogger';
import { pushSpawnDebug } from './SpawnDebugOverlay';
import { COMBO_VIDEOS, matchCombo, pickRandom } from '../config/videoConfig';
import { WPOutfit } from '../hooks/useWordPressConfig';

// ─────────────────────────────────────────────────────────────
// OUTFIT CATALOG — now sourced from WordPress
// This is kept as a minimal fallback type only.
// The real outfit list comes from the wpOutfits prop.
// ─────────────────────────────────────────────────────────────
export interface OutfitSlot {
  id: number;
  name: string;
  gifUrl: string;
  paid: boolean;
  stripeProductId?: string;
  stripePriceId?: string;
}

// Fallback single outfit used when WP hasn't loaded yet
export const OUTFIT_CATALOG: OutfitSlot[] = [
  { id: 1, name: 'Default', gifUrl: 'https://ex.plo.re/tempjmodel/wp-content/uploads/sites/858/2026/04/yes-gif.gif', paid: false },
];

// ─────────────────────────────────────────────────────────────
// COMBO VIDEO URLS
// ─────────────────────────────────────────────────────────────
const COMBO_VIDEO_URLS = COMBO_VIDEOS;

// ─────────────────────────────────────────────────────────────
// LEVEL CONFIG
// ─────────────────────────────────────────────────────────────
export interface LevelConfig {
  level: number;
  name: string;
  duration: number;
  maxObstacles: number;
  maxRamps: number;
  obstacleRecycleMin: number;
  obstacleRecycleMax: number;
  speedMultiplier: number;
  fullWidthChance: number;
  gapChance: number;
}

export const LEVEL_CONFIGS: LevelConfig[] = [
  { level: 1, name: 'Amalfi Coast',   duration: 60, maxObstacles: 3,  maxRamps: 3,  obstacleRecycleMin: 0.55, obstacleRecycleMax: 0.90, speedMultiplier: 0.60, fullWidthChance: 0.0,  gapChance: 0.0  },
  { level: 2, name: 'Mt Fuji',        duration: 75, maxObstacles: 6,  maxRamps: 4,  obstacleRecycleMin: 0.38, obstacleRecycleMax: 0.65, speedMultiplier: 0.82, fullWidthChance: 0.10, gapChance: 0.08 },
  { level: 3, name: 'Rainbow Road',   duration: 90, maxObstacles: 11, maxRamps: 6,  obstacleRecycleMin: 0.22, obstacleRecycleMax: 0.46, speedMultiplier: 1.05, fullWidthChance: 0.20, gapChance: 0.16 },
  { level: 4, name: 'Pacific Coast',  duration: 90, maxObstacles: 15, maxRamps: 9,  obstacleRecycleMin: 0.16, obstacleRecycleMax: 0.36, speedMultiplier: 1.22, fullWidthChance: 0.26, gapChance: 0.22 },
];

interface PhaserGameProps {
  controlMode: ControlMode;
  gameState: GameState;
  onHUDUpdate: (data: Partial<HUDData>) => void;
  onCrash: () => void;
  onTrickLanded: (trickName: string, points: number) => void;
  onLevelComplete: (level: number) => void;
  onOutfitUnlockPrompt: (outfit: OutfitData) => void;
  onComboVideo?: (videoSrc: string) => void;
  onIntroVideo?: () => void;
  onWinVideo?: () => void;
  onFallVideo?: () => void;
  activeOutfitId?: number;
  currentLevel?: number;
  areaId?: string;
  wpOutfits?: WPOutfit[];
}

// ── Physics constants
const GRAVITY = 0.30;           // Low gravity = big hang time for combos
const JUMP_VY = -15.5;          // Powerful jump
const BASE_SPEED = 4.2;
const MAX_SPEED = 14;           // Raised from 12 — more thrilling top speed
const SAFE_ZONE_SECONDS = 3;
const COMBO_WINDOW_MS = 3000;   // Slightly wider combo window — more satisfying chains

const COMBO_VIDEO_WINDOW = 3.0;
const COMBO_VIDEO_TRICK_THRESHOLD = 3;

// ─────────────────────────────────────────────────────────────
// TRICK KEY SYSTEM — only B and N are valid trick keys
//   B = Big Air (jump / boost)
//   N = Nosedive (spin / style)
// ─────────────────────────────────────────────────────────────
const TRICK_LIST = [
  { name: 'Big Air',  pts: 200, key: 'B' },
  { name: 'Nosedive', pts: 250, key: 'N' },
];

// ─────────────────────────────────────────────────────────────
// BANKED TURN TYPES — extreme path variety
// ─────────────────────────────────────────────────────────────
type TurnType = 'left' | 'right' | 'chicane_left' | 'chicane_right' | 'hairpin_left' | 'hairpin_right' | 'straight' | 'sweeper_left' | 'sweeper_right';

interface TurnSegment {
  type: TurnType;
  target: number;   // trackCurveOffset target (-1 to 1)
  duration: number; // seconds to hold
  urgency: number;  // 0-1, how fast the curve snaps
  obstacleHint: 'inside' | 'outside' | 'center' | 'none'; // where to place obstacles
}

export default function PhaserGame({
  controlMode,
  gameState,
  onHUDUpdate,
  onCrash,
  onTrickLanded,
  onLevelComplete,
  onOutfitUnlockPrompt,
  onComboVideo,
  onFallVideo,
  activeOutfitId = 1,
  currentLevel = 1,
  areaId = 'italy',
  wpOutfits,
}: PhaserGameProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const charImgRef = useRef<HTMLImageElement | null>(null);

  const gameStateRef = useRef(gameState);
  const controlModeRef = useRef(controlMode);
  const onHUDUpdateRef = useRef(onHUDUpdate);
  const onCrashRef = useRef(onCrash);
  const onTrickLandedRef = useRef(onTrickLanded);
  const onLevelCompleteRef = useRef(onLevelComplete);
  const onOutfitUnlockPromptRef = useRef(onOutfitUnlockPrompt);
  const onComboVideoRef = useRef(onComboVideo);
  const onFallVideoRef = useRef(onFallVideo);
  const activeOutfitIdRef = useRef(activeOutfitId);
  const currentLevelRef = useRef(currentLevel);
  const wpOutfitsRef = useRef(wpOutfits);

  useEffect(() => { gameStateRef.current = gameState; }, [gameState]);
  useEffect(() => { controlModeRef.current = controlMode; }, [controlMode]);
  useEffect(() => { onHUDUpdateRef.current = onHUDUpdate; }, [onHUDUpdate]);
  useEffect(() => { onCrashRef.current = onCrash; }, [onCrash]);
  useEffect(() => { onTrickLandedRef.current = onTrickLanded; }, [onTrickLanded]);
  useEffect(() => { onLevelCompleteRef.current = onLevelComplete; }, [onLevelComplete]);
  useEffect(() => { onOutfitUnlockPromptRef.current = onOutfitUnlockPrompt; }, [onOutfitUnlockPrompt]);
  useEffect(() => { onComboVideoRef.current = onComboVideo; }, [onComboVideo]);
  useEffect(() => { onFallVideoRef.current = onFallVideo; }, [onFallVideo]);
  useEffect(() => { activeOutfitIdRef.current = activeOutfitId; }, [activeOutfitId]);
  useEffect(() => { currentLevelRef.current = currentLevel; }, [currentLevel]);
  useEffect(() => { wpOutfitsRef.current = wpOutfits; }, [wpOutfits]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // ── Polyfill roundRect
    if (typeof ctx.roundRect !== 'function') {
      CanvasRenderingContext2D.prototype.roundRect = function(
        x: number, y: number, w: number, h: number, r: number | number[] = 0
      ) {
        const tl = typeof r === 'number' ? r : (r[0] ?? 0);
        const tr = typeof r === 'number' ? r : (r[1] ?? tl);
        const br = typeof r === 'number' ? r : (r[2] ?? tl);
        const bl = typeof r === 'number' ? r : (r[3] ?? tr);
        this.moveTo(x + tl, y);
        this.lineTo(x + w - tr, y);
        this.quadraticCurveTo(x + w, y, x + w, y + tr);
        this.lineTo(x + w, y + h - br);
        this.quadraticCurveTo(x + w, y + h, x + w - br, y + h);
        this.lineTo(x + bl, y + h);
        this.quadraticCurveTo(x, y + h, x, y + h - bl);
        this.lineTo(x, y + tl);
        this.quadraticCurveTo(x, y, x + tl, y);
        this.closePath();
        return this;
      };
    }

    const resize = () => {
      canvas.width = canvas.offsetWidth || window.innerWidth;
      canvas.height = canvas.offsetHeight || window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    function cw() { return canvas.width; }
    function ch() { return canvas.height; }

    // ── Background image
    const bgImg = new Image();
    // Area-specific background images
    const BG_IMAGES: Record<string, string> = {
      italy: '/assets/images/IMG_8032-1776974468024.jpeg',
      japan: '/assets/images/ChatGPT_Image_May_2_2026_12_15_28_PM-1777749407320.png',
      rainbow_road: '/assets/images/Screenshot_2026-05-02_at_12.15.42_PM-1777749439361.jpg',
    };
    bgImg.src = BG_IMAGES[areaId] || BG_IMAGES['italy'];
    let bgLoaded = false;
    bgImg.onload = () => { bgLoaded = true; };

    // ── Rainbow Road background (level 3 OR rainbow_road area)
    const rainbowBgImg = new Image();
    rainbowBgImg.src = '/assets/images/Screenshot_2026-05-02_at_12.15.42_PM-1777749439361.jpg';
    let rainbowBgLoaded = false;
    rainbowBgImg.onload = () => { rainbowBgLoaded = true; };

    // ── GIF character sprite
    if (charImgRef.current && charImgRef.current.parentNode) {
      charImgRef.current.parentNode.removeChild(charImgRef.current);
      charImgRef.current = null;
    }

    const charImg = document.createElement('img');
    charImg.style.cssText = 'position:fixed;left:-9999px;top:0;width:120px;height:180px;pointer-events:none;visibility:hidden;';
    document.body.appendChild(charImg);
    charImgRef.current = charImg;

    let charLoaded = false;
    let currentOutfitId = -1;

    function loadOutfitGif(outfitId: number) {
      // Prefer WP outfits (live from WordPress), fall back to local catalog
      const wpList = wpOutfitsRef.current;
      let gifUrl: string | undefined;

      if (wpList && wpList.length > 0) {
        const wpSlot = wpList.find(o => o.id === outfitId) ?? wpList[0];
        // Use previewGif for the animated character sprite; fall back to spriteSheet
        gifUrl = wpSlot.previewGif || wpSlot.spriteSheet || undefined;
        if (wpSlot.id === currentOutfitId && charLoaded) return;
        currentOutfitId = wpSlot.id;
      } else {
        const slot = OUTFIT_CATALOG.find(o => o.id === outfitId) ?? OUTFIT_CATALOG[0];
        if (slot.id === currentOutfitId && charLoaded) return;
        currentOutfitId = slot.id;
        gifUrl = slot.gifUrl;
      }

      if (!gifUrl) {
        // No image available — mark as loaded with blank so game still runs
        charLoaded = true;
        return;
      }

      charLoaded = false;
      charImg.onload = () => { charLoaded = true; };
      charImg.onerror = () => { charLoaded = false; };
      charImg.src = gifUrl;
    }
    loadOutfitGif(activeOutfitIdRef.current);

    // ── Core game state
    let score = 0;
    let health = 3;
    let distance = 0;
    let runTime = 0;
    let speed = BASE_SPEED;
    let isCrashed = false;
    let levelDone = false;
    let crashCooldown = 0;

    // Finish line
    let finishLineDepth = -2.0;
    let finishLineVisible = false;
    let finishLineCrossed = false;

    // Lateral movement
    let lateralPos = 0;
    let lateralVel = 0;
    let tiltAngle = 0;

    // Jump / air physics
    let jumpOffset = 0;
    let jumpVel = 0;
    let airborne = false;
    let airTime = 0;
    let jumpCount = 0;
    let coyoteTime = 0;
    let jumpBuffered = false;
    let jumpBufferTimer = 0;

    // Trick system
    let trickQueue: string[] = [];
    let currentTrick: string | null = null;
    let trickProgress = 0;
    let trickAnimSpeed = 3.5;
    let spinActive = false;
    let spinDeg = 0;
    let spinTarget = 360;
    let boardFlipActive = false;
    let boardFlipAngle = 0;
    let grabActive = false;
    let grabTimer = 0;
    let trickActiveDuringLanding = false;
    let lastTrickName = '';
    let trickDisplayTimer = 0;
    let trickDisplayScale = 1;
    let lastTrickPoints = 0;

    // Combo system
    let comboCount = 0;
    let comboMult = 1;
    let comboWindowMs = 0;
    let comboStreak = 0;
    let perfectLanding = false;

    // B/N tap sequence tracking
    interface ComboEvent { type: string; time: number; }
    let comboSequence: ComboEvent[] = [];
    let tapSequence: { key: string; time: number }[] = [];
    let comboVideoPlaying = false;
    let comboVideoIndex = 0;
    let comboVideoCooldown = 0;

    // Visual state
    let bgZoom = 1.0;
    let hairWave = 0;
    let shakeX = 0;
    let shakeY = 0;
    let shakeIntensity = 0;
    let screenFlashAlpha = 0;
    let screenFlashColor = '#ffffff';
    let speedLineAlpha = 0;

    // ─────────────────────────────────────────────────────────────
    // EXTREME TRACK CURVE SYSTEM
    // ─────────────────────────────────────────────────────────────
    let trackCurveOffset = 0;       // current visual offset
    let trackCurveTarget = 0;       // where we're heading
    let trackCurveTimer = 0;
    let trackCurveDuration = 2.5;   // seconds between turns
    let trackCurveUrgency = 1.0;    // snap speed multiplier
    let turnQueue: TurnSegment[] = [];
    let currentTurnIndex = 0;
    // Bank angle for visual tilt of the track walls
    let bankAngle = 0;              // degrees, positive = right bank
    let bankAngleTarget = 0;
    // Steering pressure — how hard the curve is pushing the player
    let curvePressure = 0;          // 0-1, how much the curve is forcing movement
    // Arrow indicator
    let arrowFlashTimer = 0;
    let arrowDirection: 'left' | 'right' | null = null;
    let arrowUrgency = 0;           // 0-1
    // Current turn obstacle hint for intelligent placement
    let currentObstacleHint: 'inside' | 'outside' | 'center' | 'none' = 'none';

    // Pre-generate a sequence of dramatic turns with intelligent obstacle hints
    function generateTurnSequence(progressFraction: number): TurnSegment[] {
      const intensity = 0.4 + progressFraction * 0.6;
      const turns: TurnSegment[] = [];
      let lastDir = 1;

      // Always start with a short straight so player can orient
      turns.push({ type: 'straight', target: 0, duration: 1.8, urgency: 0.6, obstacleHint: 'center' });

      for (let i = 0; i < 10; i++) {
        const roll = Math.random();
        const isHairpin = roll < 0.18 * intensity && progressFraction > 0.3;
        const isSweeper = !isHairpin && roll < 0.35 * intensity;
        const isChicane = !isHairpin && !isSweeper && roll < 0.55 * intensity;
        const dir = -lastDir;
        lastDir = dir;

        let type: TurnType;
        let target: number;
        let duration: number;
        let urgency: number;
        let obstacleHint: 'inside' | 'outside' | 'center' | 'none';

        if (isHairpin) {
          // Tight hairpin — very dramatic, obstacles block the inside
          type = dir > 0 ? 'hairpin_right' : 'hairpin_left';
          target = dir * (0.78 + Math.random() * 0.18) * intensity;
          duration = 1.0 + Math.random() * 0.7;
          urgency = 2.2 + intensity * 1.0;
          obstacleHint = 'inside'; // obstacles on inside force player to hug the outside
        } else if (isSweeper) {
          // Long sweeping bend — ramps on outside reward good steering
          type = dir > 0 ? 'sweeper_right' : 'sweeper_left';
          target = dir * (0.50 + Math.random() * 0.28) * intensity;
          duration = 2.8 + Math.random() * 1.4;
          urgency = 0.9 + intensity * 0.5;
          obstacleHint = 'outside'; // ramps on outside, obstacles scattered
        } else if (isChicane) {
          // Quick chicane — center obstacles, player must weave
          type = dir > 0 ? 'chicane_right' : 'chicane_left';
          target = dir * (0.42 + Math.random() * 0.28) * intensity;
          duration = 1.2 + Math.random() * 0.8;
          urgency = 1.6 + intensity * 0.7;
          obstacleHint = 'center';
        } else {
          // Standard banked turn
          type = dir > 0 ? 'right' : 'left';
          target = dir * (0.38 + Math.random() * 0.38) * intensity;
          duration = 1.8 + Math.random() * 1.2;
          urgency = 1.1 + intensity * 0.6;
          obstacleHint = Math.random() < 0.5 ? 'inside' : 'outside';
        }

        turns.push({ type, target, duration, urgency, obstacleHint });

        // Breathing straight between turns — shorter as game progresses
        const straightChance = 0.55 - progressFraction * 0.25;
        if (Math.random() < straightChance) {
          const straightDur = 0.6 + Math.random() * 0.8 * (1 - progressFraction * 0.5);
          turns.push({ type: 'straight', target: 0, duration: straightDur, urgency: 1.2, obstacleHint: 'none' });
        }
      }
      return turns;
    }

    // Momentum boost from tricks
    let momentumBoost = 0;

    // Particles
    interface Particle {
      x: number; y: number;
      vx: number; vy: number;
      life: number; maxLife: number;
      color: string; size: number;
      type?: 'star' | 'spark' | 'dust' | 'fire' | 'smoke';
    }
    let particles: Particle[] = [];

    // Score popups
    interface ScorePopup {
      x: number; y: number;
      text: string;
      color: string;
      vy: number;
      life: number;
      maxLife: number;
      size: number;
    }
    let scorePopups: ScorePopup[] = [];

    // ── Obstacles — EXTREME variety
    interface Obstacle {
      laneX: number;
      depth: number;
      type: number;
      animPhase: number;
      active: boolean;
      fullWidth: boolean;
      slot: number;
      warned: boolean;
      // For moving obstacles
      moveDir: number;
      moveSpeed: number;
      movePhase: number;
    }

    // RAMP TYPES: 0=standard, 1=mega, 2=kicker, 3=halfpipe, 4=launchpad, 5=double_kicker
    interface Ramp {
      laneX: number;
      depth: number;
      active: boolean;
      rampType: number;   // 0-5
      slot: number;
      used: boolean;
      glowPhase: number;
    }

    let obstacles: Obstacle[] = [];
    let ramps: Ramp[] = [];
    let hudTimer = 0;

    // Warm-up jump system
    let warmupJumpsRemaining = 2;
    let warmupJumpTimer = 0;
    const WARMUP_JUMP_INTERVAL = 1.2;

    function getLevelConfig(): LevelConfig {
      const lvl = currentLevelRef.current;
      return LEVEL_CONFIGS[Math.min(lvl - 1, LEVEL_CONFIGS.length - 1)] ?? LEVEL_CONFIGS[0];
    }

    // ── Perspective helpers
    function getTrackCenterX(): number {
      // Dramatically shift the vanishing point left/right to show the curve going INTO the distance
      return cw() * 0.5 + trackCurveOffset * cw() * 0.38;
    }

    function perspX(laneX: number, depth: number): number {
      const vanishX = getTrackCenterX();
      // Wider near-ground spread for more immersive feel — player stays centered
      const spread = cw() * 0.46 * depth;
      return vanishX + laneX * spread;
    }

    function perspY(depth: number): number {
      // Horizon higher up, ground lower — player feels more grounded/centered
      const horizonY = ch() * 0.44;
      const groundY = ch() * 0.98;
      // Steeper power curve = obstacles rush toward viewer faster (more real-world feel)
      return horizonY + (groundY - horizonY) * Math.pow(depth, 0.60);
    }

    function perspScale(depth: number): number {
      // More aggressive scale growth = objects feel like they rush at you
      return Math.max(0.04, depth * depth * 1.5);
    }

    // ── Generate obstacles — ramps and obstacles always separated
    function generateObstacles() {
      obstacles = [];
      ramps = [];
      const cfg = getLevelConfig();
      const TOTAL_OBSTACLES = 22;
      const TOTAL_RAMPS = 14;
      const initialSpacing = cfg.obstacleRecycleMin + 0.20;

      for (let i = 0; i < TOTAL_OBSTACLES; i++) {
        // Distribute across lanes with bias toward inside/outside based on hint
        const laneOptions = [-0.55, -0.28, 0, 0.28, 0.55];
        obstacles.push({
          laneX: laneOptions[i % laneOptions.length],
          depth: -(initialSpacing + i * (initialSpacing * 0.85)),
          type: i % 8,  // 8 obstacle types now
          animPhase: Math.random() * Math.PI * 2,
          active: false,
          fullWidth: false,
          slot: i,
          warned: false,
          moveDir: Math.random() < 0.5 ? 1 : -1,
          moveSpeed: 0.003 + Math.random() * 0.005,
          movePhase: Math.random() * Math.PI * 2,
        });
      }

      for (let i = 0; i < TOTAL_RAMPS; i++) {
        const laneOptions = [-0.42, -0.18, 0, 0.18, 0.42];
        const rampDepthOffset = initialSpacing * 0.55;
        ramps.push({
          laneX: laneOptions[i % laneOptions.length],
          depth: -(rampDepthOffset + i * (initialSpacing * 1.2)),
          active: false,
          rampType: i % 6,  // 6 ramp types
          slot: i,
          used: false,
          glowPhase: Math.random() * Math.PI * 2,
        });
      }
    }
    generateObstacles();

    // ── Intelligently place obstacle/ramp based on current turn hint
    function getSmartLaneX(hint: 'inside' | 'outside' | 'center' | 'none', isRamp: boolean): number {
      const curveDir = Math.sign(trackCurveOffset); // +1 = curving right, -1 = curving left
      const insideLanes = curveDir > 0 ? [0.28, 0.55] : [-0.55, -0.28];
      const outsideLanes = curveDir > 0 ? [-0.55, -0.28] : [0.28, 0.55];
      const centerLanes = [-0.28, 0, 0.28];

      if (isRamp) {
        // Ramps go on the outside of turns — reward players who steer correctly
        if (hint === 'outside' && Math.abs(trackCurveOffset) > 0.2) {
          return outsideLanes[Math.floor(Math.random() * outsideLanes.length)];
        }
        // On straights, ramps go center
        return centerLanes[Math.floor(Math.random() * centerLanes.length)];
      }

      // Obstacles
      if (hint === 'inside' && Math.abs(trackCurveOffset) > 0.2) {
        // Block the inside of the turn — player must steer to outside
        return insideLanes[Math.floor(Math.random() * insideLanes.length)];
      } else if (hint === 'center') {
        return centerLanes[Math.floor(Math.random() * centerLanes.length)];
      } else if (hint === 'outside' && Math.abs(trackCurveOffset) > 0.2) {
        // Obstacles on outside — player must hug inside
        return outsideLanes[Math.floor(Math.random() * outsideLanes.length)];
      }
      // Default: random lane
      const allLanes = [-0.55, -0.28, 0, 0.28, 0.55];
      return allLanes[Math.floor(Math.random() * allLanes.length)];
    }

    function updateProgressiveDifficulty(postSafeTime: number) {
      const cfg = getLevelConfig();
      const rampFrac = Math.min(1, postSafeTime / 25);
      const targetObs = Math.round(1 + (cfg.maxObstacles - 1) * rampFrac);
      const targetRamps = Math.round(1 + (cfg.maxRamps - 1) * rampFrac);

      let activeCount = 0;
      for (const o of obstacles) {
        o.active = activeCount < targetObs;
        if (o.active) activeCount++;
      }
      let rampCount = 0;
      for (const r of ramps) {
        r.active = rampCount < targetRamps;
        if (r.active) rampCount++;
      }
    }

    // ── Particle helpers
    function spawnParticles(x: number, y: number, color: string, count: number, crash: boolean) {
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.8;
        const spd = crash ? 3 + Math.random() * 4 : 1.5 + Math.random() * 2.5;
        particles.push({
          x, y,
          vx: Math.cos(angle) * spd,
          vy: Math.sin(angle) * spd - (crash ? 0 : 2),
          life: 1, maxLife: crash ? 0.8 : 0.55,
          color, size: crash ? 2.5 : 4,
        });
      }
    }

    function spawnStarBurst(x: number, y: number, big = false) {
      const colors = ['#ffd60a', '#ff6b6b', '#a8e6cf', '#ffffff', '#ff9f43', '#c084fc'];
      const count = big ? 30 : 20;
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count;
        const spd = (big ? 3 : 2) + Math.random() * (big ? 7 : 5);
        particles.push({
          x, y,
          vx: Math.cos(angle) * spd,
          vy: Math.sin(angle) * spd - 3,
          life: 1, maxLife: big ? 0.9 : 0.7,
          color: colors[i % colors.length],
          size: (big ? 7 : 5) + Math.random() * 3,
          type: 'star',
        });
      }
    }

    function spawnFireBurst(x: number, y: number) {
      const colors = ['#ff4400', '#ff8800', '#ffcc00', '#ff2200'];
      for (let i = 0; i < 18; i++) {
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI;
        const spd = 2 + Math.random() * 5;
        particles.push({
          x, y,
          vx: Math.cos(angle) * spd,
          vy: Math.sin(angle) * spd - 4,
          life: 1, maxLife: 0.6,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: 4 + Math.random() * 6,
          type: 'fire',
        });
      }
    }

    function spawnSmokePuff(x: number, y: number) {
      for (let i = 0; i < 8; i++) {
        particles.push({
          x: x + (Math.random() - 0.5) * 20,
          y: y + (Math.random() - 0.5) * 10,
          vx: (Math.random() - 0.5) * 1.5,
          vy: -1 - Math.random() * 2,
          life: 1, maxLife: 0.9,
          color: 'rgba(180,180,180,0.7)',
          size: 8 + Math.random() * 12,
          type: 'smoke',
        });
      }
    }

    function spawnSpeedSparks(x: number, y: number) {
      for (let i = 0; i < 8; i++) {
        particles.push({
          x, y,
          vx: -speed * 0.4 - Math.random() * 3,
          vy: (Math.random() - 0.5) * 2,
          life: 1, maxLife: 0.3,
          color: '#ffd60a',
          size: 2 + Math.random() * 2,
          type: 'spark',
        });
      }
    }

    function spawnScorePopup(x: number, y: number, text: string, color: string, big = false) {
      scorePopups.push({
        x, y,
        text,
        color,
        vy: -2.5,
        life: 1,
        maxLife: big ? 1.2 : 0.9,
        size: big ? 28 : 20,
      });
    }

    function haptic(ms: number) {
      if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(ms);
    }

    function screenFlash(color: string, alpha: number) {
      screenFlashColor = color;
      screenFlashAlpha = alpha;
    }

    // ── Trick award
    function awardTrick(name: string, pts: number) {
      comboStreak++;
      // Combo multiplier grows faster — more rewarding chains
      const streakBonus = Math.min(4, 1 + (comboStreak - 1) * 0.35);
      const total = Math.round(pts * comboMult * streakBonus);
      score += total;
      lastTrickPoints = total;
      comboCount++;
      lastTrickName = name + (comboMult > 1 ? ' x' + comboMult.toFixed(1) : '');
      trickDisplayTimer = 2.2;
      trickDisplayScale = 1.6;  // Bigger pop for more impact
      // Momentum boost scales with combo — tricks feel more powerful in a chain
      momentumBoost = Math.min(3.5, momentumBoost + 0.45 + comboStreak * 0.08);
      onHUDUpdateRef.current({ score, trickName: name, trickPoints: total, comboMultiplier: comboMult, comboCount });
      onTrickLandedRef.current(name, total);
      pushGameLog({ type: 'trick', message: name + ' +' + total + 'pts x' + comboMult, points: total, multiplier: comboMult });
      const px = cw() * 0.5 + lateralPos * cw() * 0.22;
      const py = ch() * 0.84 - jumpOffset - 80;
      spawnScorePopup(px, py, '+' + total, comboMult >= 2 ? '#ffd60a' : '#a8e6cf', comboMult >= 3);
      // Flash color scales with combo intensity
      const flashIntensity = Math.min(0.28, 0.12 + comboStreak * 0.03);
      screenFlash(comboStreak >= 4 ? '#ff00ff' : comboStreak >= 2 ? '#ff6b00' : '#ffd60a', flashIntensity);
      haptic(comboStreak >= 3 ? 45 : 25);
    }

    // ── Combo video trigger
    function checkComboVideoTrigger(tappedKey: 'B' | 'N') {
      const now = runTime;
      tapSequence = tapSequence.filter(t => now - t.time <= COMBO_VIDEO_WINDOW);
      tapSequence.push({ key: tappedKey, time: now });
      comboSequence = comboSequence.filter(ev => now - ev.time <= COMBO_VIDEO_WINDOW);
      comboSequence.push({ type: 'trick', time: now });
      const keys = tapSequence.map(t => t.key);
      const hasB = keys.includes('B');
      const hasN = keys.includes('N');
      if (!hasB || !hasN) return;
      if (comboVideoPlaying || comboVideoCooldown > 0) return;
      const matched = matchCombo(keys);
      if (matched) {
        comboVideoPlaying = true;
        comboVideoCooldown = 8.0;
        tapSequence = [];
        comboSequence = [];
        comboVideoIndex++;
        awardTrick(matched.emoji + ' ' + matched.name, matched.pts);
        const url = matched.video && matched.video.trim().length > 0
          ? matched.video
          : (pickRandom(COMBO_VIDEOS) ?? '');
        if (onComboVideoRef.current && url) onComboVideoRef.current(url);
        haptic(80);
        spawnStarBurst(cw() * 0.5, ch() * 0.65, true);
        screenFlash('#ff6b00', 0.3);
      }
    }

    // ── Crash
    function triggerCrash() {
      if (isCrashed) return;
      if (runTime < SAFE_ZONE_SECONDS) return;
      if (crashCooldown > 0) return;
      isCrashed = true;
      crashCooldown = 1.5;
      health = Math.max(0, health - 1);
      comboMult = 1; comboCount = 0; comboWindowMs = 0;
      comboSequence = []; tapSequence = []; comboStreak = 0; momentumBoost = 0;
      shakeIntensity = 22;
      boardFlipActive = false; grabActive = false; trickActiveDuringLanding = false;
      spinActive = false;
      spawnParticles(cw() * 0.5, ch() * 0.84, '#ff4d6d', 32, true);
      screenFlash('#ff0000', 0.4);
      haptic(60);
      pushGameLog({ type: 'crash', message: 'CRASH! HP:' + health + '/3' });
      onCrashRef.current();
      if (onFallVideoRef.current) onFallVideoRef.current();
    }

    // ── Input handlers
    function onJump() {
      if (isCrashed || gameStateRef.current !== 'running') return;
      if (airborne && jumpCount < 1) {
        const now = runTime;
        const recentB = tapSequence.filter(t => t.key === 'B' && now - t.time <= 0.6);
        if (recentB.length >= 1) {
          awardTrick('Double Big', 400);
        } else {
          awardTrick('Big Air', 200);
        }
        jumpVel = JUMP_VY * 0.75;
        jumpCount++;
        airborne = true;
        trickActiveDuringLanding = false;
        boardFlipActive = true;
        boardFlipAngle = 0;
        grabActive = true;
        grabTimer = 0.35;
        shakeIntensity = 3;
        spawnStarBurst(cw() * 0.5 + lateralPos * cw() * 0.22, ch() * 0.84 - jumpOffset - 60);
        spawnParticles(cw() * 0.5 + lateralPos * cw() * 0.22, ch() * 0.84 - jumpOffset, '#c0e0ff', 12, false);
        checkComboVideoTrigger('B');
        haptic(20);
        return;
      }
      if (!airborne || coyoteTime > 0) {
        jumpVel = JUMP_VY;
        airborne = true;
        jumpCount = 0;
        coyoteTime = 0;
        trickActiveDuringLanding = false;
        comboSequence.push({ type: 'jump', time: runTime });
        spawnParticles(cw() * 0.5 + lateralPos * cw() * 0.22, ch() * 0.84, '#a8e6cf', 14, false);
        spawnSpeedSparks(cw() * 0.5 + lateralPos * cw() * 0.22, ch() * 0.84);
        haptic(12);
      } else {
        jumpBuffered = true;
        jumpBufferTimer = 0.15;
      }
    }

    function onSpin() {
      if (isCrashed || gameStateRef.current !== 'running') return;
      if (airborne) {
        const now = runTime;
        const recentN = tapSequence.filter(t => t.key === 'N' && now - t.time <= 0.6);
        if (!spinActive) {
          spinActive = true;
          spinDeg = 0;
          spinTarget = recentN.length >= 1 ? 720 : 360;
          trickActiveDuringLanding = true;
          const trickName = recentN.length >= 1 ? 'Double Nose' : 'Nosedive';
          const trickPts = recentN.length >= 1 ? 500 : 250;
          awardTrick(trickName, trickPts);
          checkComboVideoTrigger('N');
          haptic(20);
        } else {
          spinTarget += 360;
          awardTrick('Nosedive', 250);
          checkComboVideoTrigger('N');
          haptic(15);
        }
      }
    }

    function onTrick() {
      if (isCrashed || gameStateRef.current !== 'running') return;
      if (airborne && airTime > 0.06) {
        awardTrick('Big Air', 200);
        boardFlipActive = true;
        boardFlipAngle = 0;
        trickActiveDuringLanding = true;
        grabActive = true;
        grabTimer = 0.45;
        shakeIntensity = 4;
        checkComboVideoTrigger('B');
        spawnStarBurst(cw() * 0.5 + lateralPos * cw() * 0.22, ch() * 0.84 - jumpOffset - 60);
        haptic(30);
      }
    }

    function onComboBtn() {
      if (isCrashed || gameStateRef.current !== 'running') return;
      if (comboWindowMs > 0) {
        comboCount++;
        comboMult = Math.min(8, comboMult + 0.5);
        comboWindowMs = COMBO_WINDOW_MS;
        spawnParticles(cw() * 0.5, ch() * 0.84 - jumpOffset - 40, '#a8e6cf', 10, false);
        haptic(20);
      } else if (airborne) {
        onTrick();
      }
    }

    function onTiltEvt(e: Event) {
      const ce = e as CustomEvent;
      if (ce.detail?.direction === 'left') tiltAngle = -32;
      else if (ce.detail?.direction === 'right') tiltAngle = 32;
      else tiltAngle = 0;
    }

    function onRestart() {
      score = 0; health = 3; distance = 0; runTime = 0;
      speed = BASE_SPEED; isCrashed = false; levelDone = false;
      jumpOffset = 0; jumpVel = 0; airborne = false; airTime = 0;
      jumpCount = 0; coyoteTime = 0; jumpBuffered = false; jumpBufferTimer = 0;
      lateralPos = 0; lateralVel = 0;
      comboCount = 0; comboMult = 1; comboWindowMs = 0; comboStreak = 0;
      spinActive = false; boardFlipActive = false; grabActive = false;
      comboSequence = []; comboVideoPlaying = false; comboVideoCooldown = 0;
      particles = []; scorePopups = []; bgZoom = 1.0; momentumBoost = 0;
      trackCurveOffset = 0; trackCurveTarget = 0; trackCurveTimer = 0;
      trackCurveUrgency = 1.0; turnQueue = []; currentTurnIndex = 0;
      bankAngle = 0; bankAngleTarget = 0; curvePressure = 0;
      arrowFlashTimer = 0; arrowDirection = null; arrowUrgency = 0;
      lastTrickName = ''; trickDisplayTimer = 0; crashCooldown = 0;
      finishLineDepth = -2.0; finishLineVisible = false; finishLineCrossed = false;
      screenFlashAlpha = 0; speedLineAlpha = 0;
      warmupJumpsRemaining = 2; warmupJumpTimer = 0;
      generateObstacles();
      loadOutfitGif(activeOutfitIdRef.current);
    }

    function onComboVideoEnd() {
      comboVideoPlaying = false;
    }

    function onDeviceOrientation(e: DeviceOrientationEvent) {
      if (gameStateRef.current !== 'running') return;
      tiltAngle = e.gamma !== null ? e.gamma : 0;
    }

    function onOutfitChange(e: Event) {
      const ce = e as CustomEvent;
      if (typeof ce.detail?.outfitId === 'number') loadOutfitGif(ce.detail.outfitId);
    }

    window.addEventListener('surfsterre_jump', onJump);
    window.addEventListener('surfsterre_spin', onSpin);
    window.addEventListener('surfsterre_trick', onTrick);
    window.addEventListener('surfsterre_combo', onComboBtn);
    window.addEventListener('surfsterre_tilt', onTiltEvt);
    window.addEventListener('surfsterre_restart', onRestart);
    window.addEventListener('surfsterre_combo_video_end', onComboVideoEnd);
    window.addEventListener('surfsterre_outfit_change', onOutfitChange);
    if (controlModeRef.current === 'tilt') {
      window.addEventListener('deviceorientation', onDeviceOrientation);
    }

    // ── Keyboard controls
    const keysDown = new Set<string>();

    function onKeyDown(e: KeyboardEvent) {
      if (gameStateRef.current !== 'running') return;
      const key = e.code || e.key;
      keysDown.add(key);
      if (key === 'Space' || key === 'KeyB') { e.preventDefault(); onJump(); }
      if (key === 'KeyN') { e.preventDefault(); onSpin(); }
    }

    function onKeyUp(e: KeyboardEvent) {
      const key = e.code || e.key;
      keysDown.delete(key);
      if (key === 'ArrowLeft' || key === 'ArrowRight') {
        if (!keysDown.has('ArrowLeft') && !keysDown.has('ArrowRight')) tiltAngle = 0;
      }
    }

    const keysDownRef = { current: keysDown };
    (window as unknown as Record<string, unknown>).__surfsterre_keys = keysDownRef;

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    pushGameLog({ type: 'system', message: 'Level ' + currentLevelRef.current + ' — ' + getLevelConfig().name });

    // ─────────────────────────────────────────────────────────────
    // DRAW: Background
    // ─────────────────────────────────────────────────────────────
    let bgZoomLogTimer = 0;
    function drawBackground(ox: number, oy: number) {
      const w = cw();
      const h = ch();

      // Level 3 = Rainbow Road — use its own background
      const isRainbowRoad = currentLevelRef.current === 3 || areaId === 'rainbow_road';

      if (isRainbowRoad) {
        // ── RAINBOW ROAD: deep black space background
        ctx.fillStyle = '#000008';
        ctx.fillRect(ox, oy, w, h);

        // Starfield — fills upper 50% (sky area above horizon at 44%)
        const starSeed = 42;
        const starCount = 200;
        for (let si = 0; si < starCount; si++) {
          const sx = ((starSeed * (si * 7 + 3)) % 997) / 997;
          const sy = ((starSeed * (si * 13 + 7)) % 991) / 991;
          const ss = ((starSeed * (si * 5 + 11)) % 983) / 983;
          const twinkle = 0.4 + Math.sin(runTime * (1 + ss * 3) + si) * 0.3;
          ctx.globalAlpha = twinkle * (0.5 + ss * 0.5);
          ctx.fillStyle = ss > 0.7 ? '#fffbe0' : ss > 0.4 ? '#c0d8ff' : '#ffffff';
          const starSize = 0.8 + ss * 2.2;
          ctx.beginPath();
          // Stars fill the upper 50% of screen
          ctx.arc(ox + sx * w, oy + sy * h * 0.50, starSize, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;

        // Nebula clouds (left and right sides only — path is center)
        const nebulaColors = [
          ['rgba(80,0,160,0.18)', 'rgba(160,0,80,0.12)'],
          ['rgba(0,60,160,0.15)', 'rgba(80,0,120,0.10)'],
        ];
        for (let ni = 0; ni < 2; ni++) {
          const nx = ni === 0 ? ox + w * 0.12 : ox + w * 0.88;
          const ny = oy + h * 0.22;
          const nebGrad = ctx.createRadialGradient(nx, ny, 10, nx, ny, w * 0.28);
          nebGrad.addColorStop(0, nebulaColors[ni][0]);
          nebGrad.addColorStop(1, nebulaColors[ni][1]);
          ctx.fillStyle = nebGrad;
          ctx.fillRect(ox, oy, w, h * 0.55);
        }

        // Distant planet (left side)
        const planetX = ox + w * 0.1;
        const planetY = oy + h * 0.18;
        const planetR = w * 0.055;
        const planetGrad = ctx.createRadialGradient(planetX - planetR * 0.3, planetY - planetR * 0.3, planetR * 0.1, planetX, planetY, planetR);
        planetGrad.addColorStop(0, '#c060ff');
        planetGrad.addColorStop(0.5, '#7020c0');
        planetGrad.addColorStop(1, '#200040');
        ctx.fillStyle = planetGrad;
        ctx.beginPath();
        ctx.arc(planetX, planetY, planetR, 0, Math.PI * 2);
        ctx.fill();
        // Planet ring
        ctx.save();
        ctx.translate(planetX, planetY);
        ctx.rotate(-0.3);
        ctx.strokeStyle = 'rgba(200,120,255,0.45)';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.ellipse(0, 0, planetR * 1.7, planetR * 0.35, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();

        // Speed lines at high speed
        if (speed > MAX_SPEED * 0.4) {
          speedLineAlpha = Math.min(0.55, speedLineAlpha + 0.04);
          const lineCount = 14;
          ctx.strokeStyle = 'rgba(180,100,255,' + speedLineAlpha * 0.5 + ')';
          ctx.lineWidth = 1;
          for (let li = 0; li < lineCount; li++) {
            const lx = (li / lineCount) * w + ox;
            const len = 30 + (speed / MAX_SPEED) * 90;
            ctx.beginPath();
            ctx.moveTo(lx, oy + h * 0.3 + li * 15);
            ctx.lineTo(lx - len, oy + h * 0.3 + li * 15);
            ctx.stroke();
          }
        } else {
          speedLineAlpha = Math.max(0, speedLineAlpha - 0.03);
        }

      } else {
        // ── Normal areas: use background image
        const activeBgImg = bgImg;
        const activeBgLoaded = bgLoaded;

        if (activeBgLoaded) {
          const zoomProgress = Math.min(1, runTime / getLevelConfig().duration);
          bgZoom = 1.0 + zoomProgress * 0.65;
          bgZoomLogTimer += 0.016;
          if (bgZoomLogTimer >= 5) { bgZoomLogTimer = 0; }

          const srcW = activeBgImg.naturalWidth / bgZoom;
          const srcH = activeBgImg.naturalHeight / bgZoom;
          const panX = trackCurveOffset * 0.06;
          const srcX = Math.max(0, Math.min(activeBgImg.naturalWidth - srcW, (activeBgImg.naturalWidth - srcW) * (0.5 + panX)));
          const srcY = Math.max(0, Math.min(activeBgImg.naturalHeight - srcH, (activeBgImg.naturalHeight - srcH) * 0.25));
          ctx.drawImage(activeBgImg, srcX, srcY, srcW, srcH, ox, oy, w, h);

          const vignette = ctx.createRadialGradient(ox + w * 0.5, oy + h * 0.5, h * 0.15, ox + w * 0.5, oy + h * 0.5, h * 0.9);
          vignette.addColorStop(0, 'rgba(0,0,0,0)');
          vignette.addColorStop(1, 'rgba(0,0,0,0.45)');
          ctx.fillStyle = vignette;
          ctx.fillRect(ox, oy, w, h);

          if (speed > MAX_SPEED * 0.55) {
            const blurAlpha = ((speed - MAX_SPEED * 0.55) / (MAX_SPEED * 0.45)) * 0.14;
            ctx.fillStyle = 'rgba(255,255,255,' + blurAlpha + ')';
            ctx.fillRect(ox, oy, w, h);
          }
        } else {
          const skyGrad = ctx.createLinearGradient(ox, oy, ox, oy + h);
          skyGrad.addColorStop(0, '#4a6fa5');
          skyGrad.addColorStop(0.4, '#7a9ec8');
          skyGrad.addColorStop(0.6, '#8ab890');
          skyGrad.addColorStop(1, '#5a8840');
          ctx.fillStyle = skyGrad;
          ctx.fillRect(ox, oy, w, h);
        }

        if (speed > MAX_SPEED * 0.4) {
          speedLineAlpha = Math.min(0.45, speedLineAlpha + 0.04);
          const lineCount = 12;
          ctx.strokeStyle = 'rgba(200,230,255,' + speedLineAlpha * 0.6 + ')';
          ctx.lineWidth = 1;
          for (let li = 0; li < lineCount; li++) {
            const lx = (li / lineCount) * w + ox;
            const len = 30 + (speed / MAX_SPEED) * 80;
            ctx.beginPath();
            ctx.moveTo(lx, oy + h * 0.3 + li * 15);
            ctx.lineTo(lx - len, oy + h * 0.3 + li * 15);
            ctx.stroke();
          }
        } else {
          speedLineAlpha = Math.max(0, speedLineAlpha - 0.03);
        }
      }

      if (screenFlashAlpha > 0) {
        ctx.fillStyle = screenFlashColor;
        ctx.globalAlpha = screenFlashAlpha;
        ctx.fillRect(ox, oy, w, h);
        ctx.globalAlpha = 1;
        screenFlashAlpha = Math.max(0, screenFlashAlpha - 0.06);
      }
    }

    // ─────────────────────────────────────────────────────────────
    // DRAW: Extreme banked track with visible bank walls
    // ─────────────────────────────────────────────────────────────
    function drawSlope(ox: number, oy: number) {
      const w = cw();
      const h = ch();
      // Horizon lower = more sky visible, more immersive road-ahead feel
      const horizonY = oy + ch() * 0.44;
      // The vanishing point shifts dramatically with the curve — this IS the visual curve
      const vanishX = ox + getTrackCenterX();

      // Wider track near-ground = player feels more centered on the road
      const trackHalfNear = w * 0.50;
      const trackHalfFar = w * 0.036;

      // Bank tilt — visual lean of the entire track during turns
      const bankRad = (bankAngle * Math.PI) / 180;
      const bankShiftNear = Math.sin(bankRad) * trackHalfNear * 0.28;
      const bankShiftFar = Math.sin(bankRad) * trackHalfFar * 0.28;

      const isRainbowRoad = currentLevelRef.current === 3 || areaId === 'rainbow_road';

      if (isRainbowRoad) {
        // ── RAINBOW ROAD: Black space void on sides, rainbow stripe path in center

        // Left void (black space)
        ctx.fillStyle = '#000008';
        ctx.beginPath();
        ctx.moveTo(ox, horizonY);
        ctx.lineTo(vanishX - trackHalfFar + bankShiftFar, horizonY);
        ctx.lineTo(ox + w * 0.5 - trackHalfNear + trackCurveOffset * w * 0.38 + bankShiftNear, oy + h);
        ctx.lineTo(ox, oy + h);
        ctx.closePath();
        ctx.fill();

        // Right void (black space)
        ctx.fillStyle = '#000008';
        ctx.beginPath();
        ctx.moveTo(ox + w, horizonY);
        ctx.lineTo(vanishX + trackHalfFar + bankShiftFar, horizonY);
        ctx.lineTo(ox + w * 0.5 + trackHalfNear + trackCurveOffset * w * 0.38 + bankShiftNear, oy + h);
        ctx.lineTo(ox + w, oy + h);
        ctx.closePath();
        ctx.fill();

        // Stars in the void areas (scattered)
        const voidStarCount = 30;
        for (let vsi = 0; vsi < voidStarCount; vsi++) {
          const vseed = vsi * 17 + 3;
          const vsx = ((vseed * 7) % 100) / 100;
          const vsy = ((vseed * 13) % 100) / 100;
          const vss = ((vseed * 5) % 100) / 100;
          const twinkle = 0.3 + Math.sin(runTime * (1 + vss * 2) + vsi * 0.7) * 0.3;
          ctx.globalAlpha = twinkle;
          ctx.fillStyle = '#ffffff';
          // Place in left or right void
          const isLeft = vsi < voidStarCount / 2;
          const voidX = isLeft
            ? ox + vsx * (w * 0.5 - trackHalfNear * 0.6)
            : ox + w * 0.5 + trackHalfNear * 0.6 + vsx * (w * 0.5 - trackHalfNear * 0.6);
          const voidY = oy + horizonY * 0.3 + vsy * (h - horizonY * 0.3);
          ctx.beginPath();
          ctx.arc(voidX, voidY, 0.8 + vss * 1.5, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;

        // ── Rainbow stripe path — 7 color bands scrolling toward viewer
        const rainbowColors = [
          '#ff0000', // red
          '#ff7700', // orange
          '#ffee00', // yellow
          '#00cc00', // green
          '#0088ff', // blue
          '#6600cc', // indigo
          '#cc00ff', // violet
        ];
        const stripeCount = rainbowColors.length;
        const scrollOffset = (runTime * speed * 60 * 0.012) % 1;
        const patchCount = 28;

        for (let row = 0; row < patchCount; row++) {
          const rowT = ((row / patchCount) + scrollOffset) % 1;
          const rowDepth = Math.pow(rowT, 0.50);
          const rowY = horizonY + (oy + h - horizonY) * rowDepth;
          const rowHalfW = trackHalfFar + (trackHalfNear - trackHalfFar) * rowDepth;
          const rowCurveShift = trackCurveOffset * w * 0.38 * rowDepth + bankShiftNear * rowDepth;
          const nextRowT = ((( row + 1) / patchCount) + scrollOffset) % 1;
          const nextRowDepth = Math.pow(nextRowT, 0.50);
          const nextRowY = horizonY + (oy + h - horizonY) * nextRowDepth;
          const nextRowHalfW = trackHalfFar + (trackHalfNear - trackHalfFar) * nextRowDepth;
          const nextRowCurveShift = trackCurveOffset * w * 0.38 * nextRowDepth + bankShiftNear * nextRowDepth;

          // Each row is one stripe color, cycling through rainbow
          const colorIndex = (row + Math.floor(runTime * 8)) % stripeCount;
          const stripeColor = rainbowColors[colorIndex];

          // Draw the stripe band as a trapezoid
          ctx.fillStyle = stripeColor;
          ctx.globalAlpha = 0.85 + Math.sin(runTime * 4 + row * 0.5) * 0.1;
          ctx.beginPath();
          ctx.moveTo(vanishX - rowHalfW + rowCurveShift, rowY);
          ctx.lineTo(vanishX + rowHalfW + rowCurveShift, rowY);
          ctx.lineTo(vanishX + nextRowHalfW + nextRowCurveShift, nextRowY);
          ctx.lineTo(vanishX - nextRowHalfW + nextRowCurveShift, nextRowY);
          ctx.closePath();
          ctx.fill();
        }
        ctx.globalAlpha = 1;

        // Glowing path edges
        ctx.save();
        ctx.shadowColor = '#ffffff';
        ctx.shadowBlur = 12;
        ctx.strokeStyle = 'rgba(255,255,255,0.7)';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(vanishX - trackHalfFar + bankShiftFar, horizonY);
        ctx.lineTo(ox + w * 0.5 - trackHalfNear + trackCurveOffset * w * 0.38 + bankShiftNear, oy + h);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(vanishX + trackHalfFar + bankShiftFar, horizonY);
        ctx.lineTo(ox + w * 0.5 + trackHalfNear + trackCurveOffset * w * 0.38 + bankShiftNear, oy + h);
        ctx.stroke();
        ctx.shadowBlur = 0;
        ctx.restore();

        // Floating rainbow road edge glow (psychedelic aura)
        const edgePulse = 0.4 + Math.sin(runTime * 3) * 0.3;
        ctx.save();
        ctx.globalAlpha = edgePulse * 0.6;
        const leftEdgeGrad = ctx.createLinearGradient(
          ox + w * 0.5 - trackHalfNear + trackCurveOffset * w * 0.38, oy + h,
          ox + w * 0.5 - trackHalfNear + trackCurveOffset * w * 0.38 - 30, oy + h
        );
        leftEdgeGrad.addColorStop(0, 'rgba(255,100,255,0.8)');
        leftEdgeGrad.addColorStop(1, 'rgba(255,100,255,0)');
        ctx.fillStyle = leftEdgeGrad;
        ctx.fillRect(ox, oy + h * 0.5, w * 0.5, h * 0.5);
        ctx.restore();

        // Turn direction arrows on rainbow road
        if (Math.abs(trackCurveOffset) > 0.06 && !isCrashed) {
          const arrowDir = trackCurveOffset > 0 ? 1 : -1;
          const arrowIntensity = Math.min(1, Math.abs(trackCurveOffset) / 0.55);
          const arrowCount = 5;
          for (let ai = 0; ai < arrowCount; ai++) {
            const t = 0.18 + (ai / arrowCount) * 0.65;
            const ay = horizonY + (oy + h - horizonY) * Math.pow(t, 0.68);
            const ax = vanishX + trackCurveOffset * w * 0.38 * t + bankShiftNear * t;
            const as = perspScale(t) * 65;
            const pulse = 0.55 + Math.sin(runTime * 5 + ai * 0.8) * 0.45;
            ctx.save();
            ctx.globalAlpha = arrowIntensity * pulse * 0.92;
            ctx.fillStyle = '#ffffff';
            ctx.font = `bold ${Math.round(as)}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.shadowColor = '#ff00ff';
            ctx.shadowBlur = 10;
            ctx.fillText(arrowDir > 0 ? '➡' : '⬅', ax, ay + as * 0.3);
            ctx.shadowBlur = 0;
            ctx.globalAlpha = 1;
            ctx.restore();
          }
        }

      } else {
        // ── Normal track (Italy / Japan)

        // Outer terrain — Italian countryside: green rolling hills
        const grassLeft = ctx.createLinearGradient(ox, horizonY, ox, oy + h);
        grassLeft.addColorStop(0, 'rgba(72,110,42,0.95)');
        grassLeft.addColorStop(0.35, 'rgba(88,130,52,0.97)');
        grassLeft.addColorStop(0.7, 'rgba(60,95,32,0.98)');
        grassLeft.addColorStop(1, 'rgba(45,75,22,1.0)');
        ctx.fillStyle = grassLeft;
        ctx.beginPath();
        ctx.moveTo(ox, horizonY);
        ctx.lineTo(vanishX - trackHalfFar + bankShiftFar, horizonY);
        ctx.lineTo(ox + w * 0.5 - trackHalfNear + trackCurveOffset * w * 0.38 + bankShiftNear, oy + h);
        ctx.lineTo(ox, oy + h);
        ctx.closePath();
        ctx.fill();

        const grassRight = ctx.createLinearGradient(ox + w, horizonY, ox + w, oy + h);
        grassRight.addColorStop(0, 'rgba(72,110,42,0.95)');
        grassRight.addColorStop(0.35, 'rgba(88,130,52,0.97)');
        grassRight.addColorStop(0.7, 'rgba(60,95,32,0.98)');
        grassRight.addColorStop(1, 'rgba(45,75,22,1.0)');
        ctx.fillStyle = grassRight;
        ctx.beginPath();
        ctx.moveTo(ox + w, horizonY);
        ctx.lineTo(vanishX + trackHalfFar + bankShiftFar, horizonY);
        ctx.lineTo(ox + w * 0.5 + trackHalfNear + trackCurveOffset * w * 0.38 + bankShiftNear, oy + h);
        ctx.lineTo(ox + w, oy + h);
        ctx.closePath();
        ctx.fill();

        // Roadside scenery dots (cypress trees, sunflowers) on the grass banks
        const sceneCount = 10;
        for (let si = 0; si < sceneCount; si++) {
          const t = (si / sceneCount);
          const rowDepth = Math.pow(t, 0.55);
          const rowY = horizonY + (oy + h - horizonY) * rowDepth;
          const rowHalfW = trackHalfFar + (trackHalfNear - trackHalfFar) * rowDepth;
          const rowCurveShift = trackCurveOffset * w * 0.38 * rowDepth + bankShiftNear * rowDepth;
          const sc2 = perspScale(rowDepth);
          const treeSize = Math.max(6, sc2 * 55);
          // Left side cypress
          const lx = vanishX - rowHalfW * 1.22 + rowCurveShift;
          ctx.fillStyle = 'rgba(30,80,20,0.85)';
          ctx.beginPath();
          ctx.ellipse(lx, rowY - treeSize * 0.5, treeSize * 0.22, treeSize * 0.55, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = 'rgba(60,40,20,0.7)';
          ctx.fillRect(lx - treeSize * 0.06, rowY, treeSize * 0.12, treeSize * 0.25);
          // Right side cypress
          const rx = vanishX + rowHalfW * 1.22 + rowCurveShift;
          ctx.fillStyle = 'rgba(30,80,20,0.85)';
          ctx.beginPath();
          ctx.ellipse(rx, rowY - treeSize * 0.5, treeSize * 0.22, treeSize * 0.55, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = 'rgba(60,40,20,0.7)';
          ctx.fillRect(rx - treeSize * 0.06, rowY, treeSize * 0.12, treeSize * 0.25);
        }

        // BANKED WALLS — visible stone walls on the inside of turns (Italian cobblestone)
        if (Math.abs(trackCurveOffset) > 0.08) {
          const wallSide = trackCurveOffset > 0 ? 1 : -1;
          const wallIntensity = Math.min(1, Math.abs(trackCurveOffset) / 0.65);
          const wallAlpha = 0.65 + wallIntensity * 0.30;

          const wallGrad = ctx.createLinearGradient(
            vanishX + wallSide * trackHalfFar * 0.3, horizonY,
            vanishX + wallSide * trackHalfNear * 0.3 + trackCurveOffset * w * 0.38, oy + h
          );
          wallGrad.addColorStop(0, `rgba(160,130,90,${wallAlpha * 0.4})`);
          wallGrad.addColorStop(0.4, `rgba(${wallIntensity > 0.7 ? '200,80,30' : '180,150,100'},${wallAlpha})`);
          wallGrad.addColorStop(1, `rgba(190,155,105,${wallAlpha})`);
          ctx.fillStyle = wallGrad;
          ctx.beginPath();
          ctx.moveTo(vanishX + wallSide * trackHalfFar * 0.3 + bankShiftFar, horizonY);
          ctx.lineTo(vanishX + wallSide * trackHalfFar + bankShiftFar, horizonY);
          ctx.lineTo(ox + w * 0.5 + wallSide * trackHalfNear + trackCurveOffset * w * 0.38 + bankShiftNear, oy + h);
          ctx.lineTo(ox + w * 0.5 + wallSide * trackHalfNear * 0.3 + trackCurveOffset * w * 0.38 + bankShiftNear, oy + h);
          ctx.closePath();
          ctx.fill();

          const stoneAlpha = wallIntensity * 0.55;
          ctx.strokeStyle = `rgba(120,95,65,${stoneAlpha})`;
          ctx.lineWidth = Math.max(1, wallIntensity * 3);
          for (let li = 0; li < 5; li++) {
            const lt = (li + 0.5) / 5;
            const ly = horizonY + (oy + h - horizonY) * Math.pow(lt, 0.68);
            const lx1 = vanishX + wallSide * (trackHalfFar * 0.3 + (trackHalfFar * 0.7) * lt) + trackCurveOffset * w * 0.38 * lt + bankShiftNear * lt;
            const lx2 = vanishX + wallSide * (trackHalfFar + (trackHalfNear - trackHalfFar) * lt) + trackCurveOffset * w * 0.38 * lt + bankShiftNear * lt;
            ctx.beginPath();
            ctx.moveTo(lx1, ly);
            ctx.lineTo(lx2, ly);
            ctx.stroke();
          }

          if (wallIntensity > 0.5) {
            const chevronAlpha = (wallIntensity - 0.5) * 2.0;
            const chevronCount = 5;
            for (let ci = 0; ci < chevronCount; ci++) {
              const t = (ci + 0.5) / chevronCount;
              const cy = horizonY + (oy + h - horizonY) * Math.pow(t, 0.68);
              const cx = vanishX + wallSide * (trackHalfFar + (trackHalfNear - trackHalfFar) * t) * 0.92 + trackCurveOffset * w * 0.38 * t + bankShiftNear * t;
              const cs = perspScale(t) * 28;
              ctx.save();
              ctx.globalAlpha = chevronAlpha * Math.min(1, t * 3);
              ctx.fillStyle = wallIntensity > 0.75 ? '#ff2200' : '#ffaa00';
              ctx.font = `bold ${Math.round(cs)}px sans-serif`;
              ctx.textAlign = 'center';
              ctx.fillText(wallSide > 0 ? '▶' : '◀', cx, cy);
              ctx.globalAlpha = 1;
              ctx.restore();
            }
          }

          const outerSide = -wallSide;
          const rumbleCount = 8;
          for (let ri = 0; ri < rumbleCount; ri++) {
            const t = (ri + 0.5) / rumbleCount;
            const ry = horizonY + (oy + h - horizonY) * Math.pow(t, 0.68);
            const rx = vanishX + outerSide * (trackHalfFar + (trackHalfNear - trackHalfFar) * t) * 0.96 + trackCurveOffset * w * 0.38 * t + bankShiftNear * t;
            const rw = Math.max(3, perspScale(t) * 22);
            const rh = Math.max(2, perspScale(t) * 8);
            ctx.fillStyle = ri % 2 === 0 ? 'rgba(255,30,30,0.75)' : 'rgba(255,255,255,0.75)';
            ctx.fillRect(rx - rw / 2, ry - rh / 2, rw, rh);
          }
        }

        // Main cobblestone path
        const dirtGrad = ctx.createLinearGradient(vanishX, horizonY, vanishX, oy + h);
        dirtGrad.addColorStop(0, 'rgba(148,132,108,0.92)');
        dirtGrad.addColorStop(0.25, 'rgba(162,145,118,0.94)');
        dirtGrad.addColorStop(0.55, 'rgba(172,155,125,0.96)');
        dirtGrad.addColorStop(0.85, 'rgba(180,162,130,0.97)');
        dirtGrad.addColorStop(1, 'rgba(188,168,136,0.98)');
        ctx.fillStyle = dirtGrad;
        ctx.beginPath();
        ctx.moveTo(vanishX - trackHalfFar + bankShiftFar, horizonY);
        ctx.lineTo(vanishX + trackHalfFar + bankShiftFar, horizonY);
        ctx.lineTo(ox + w * 0.5 + trackHalfNear + trackCurveOffset * w * 0.38 + bankShiftNear, oy + h);
        ctx.lineTo(ox + w * 0.5 - trackHalfNear + trackCurveOffset * w * 0.38 + bankShiftNear, oy + h);
        ctx.closePath();
        ctx.fill();

        // Cobblestone texture
        const scrollOffset = (runTime * speed * 60 * 0.012) % 1;
        const patchCount = 20;
        for (let row = 0; row < patchCount; row++) {
          const rowT = ((row / patchCount) + scrollOffset) % 1;
          const rowDepth = Math.pow(rowT, 0.50);
          const rowY = horizonY + (oy + h - horizonY) * rowDepth;
          const rowHalfW = trackHalfFar + (trackHalfNear - trackHalfFar) * rowDepth;
          const rowCurveShift = trackCurveOffset * w * 0.38 * rowDepth + bankShiftNear * rowDepth;
          const stoneW = Math.max(4, rowDepth * 28);
          const stoneH = Math.max(2, rowDepth * 12);
          const stoneCount = Math.max(3, Math.round(rowHalfW * 2 / (stoneW * 1.1)));
          for (let si = 0; si < stoneCount; si++) {
            const sx2 = vanishX - rowHalfW + rowCurveShift + (si + 0.5) * (rowHalfW * 2 / stoneCount);
            const offset = (row % 2 === 0 ? 0 : stoneW * 0.5);
            const stoneAlpha = 0.12 + (si % 3) * 0.06;
            ctx.fillStyle = `rgba(120,100,75,${stoneAlpha})`;
            ctx.beginPath();
            ctx.roundRect(sx2 + offset - stoneW * 0.45, rowY - stoneH * 0.5, stoneW * 0.88, stoneH * 0.85, 2);
            ctx.fill();
          }
          ctx.strokeStyle = `rgba(100,82,58,0.22)`;
          ctx.lineWidth = Math.max(1, rowDepth * 2);
          ctx.beginPath();
          ctx.moveTo(vanishX - rowHalfW + rowCurveShift, rowY);
          ctx.lineTo(vanishX + rowHalfW + rowCurveShift, rowY);
          ctx.stroke();
        }

        // Center dividing line
        ctx.strokeStyle = 'rgba(255,255,255,0.18)';
        ctx.lineWidth = Math.max(1, 2.5);
        ctx.setLineDash([18, 14]);
        ctx.beginPath();
        ctx.moveTo(vanishX + bankShiftFar, horizonY);
        ctx.lineTo(ox + w * 0.5 + trackCurveOffset * w * 0.38 + bankShiftNear, oy + h);
        ctx.stroke();
        ctx.setLineDash([]);

        // Track edges
        ctx.strokeStyle = 'rgba(100,80,50,0.88)';
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.moveTo(vanishX - trackHalfFar + bankShiftFar, horizonY);
        ctx.lineTo(ox + w * 0.5 - trackHalfNear + trackCurveOffset * w * 0.38 + bankShiftNear, oy + h);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(vanishX + trackHalfFar + bankShiftFar, horizonY);
        ctx.lineTo(ox + w * 0.5 + trackHalfNear + trackCurveOffset * w * 0.38 + bankShiftNear, oy + h);
        ctx.stroke();

        // Turn direction arrows on the track surface
        if (Math.abs(trackCurveOffset) > 0.06 && !isCrashed) {
          const arrowDir = trackCurveOffset > 0 ? 1 : -1;
          const arrowIntensity = Math.min(1, Math.abs(trackCurveOffset) / 0.55);
          const arrowCount = 5;
          for (let ai = 0; ai < arrowCount; ai++) {
            const t = 0.18 + (ai / arrowCount) * 0.65;
            const ay = horizonY + (oy + h - horizonY) * Math.pow(t, 0.68);
            const ax = vanishX + trackCurveOffset * w * 0.38 * t + bankShiftNear * t;
            const as = perspScale(t) * 65;
            const pulse = 0.55 + Math.sin(runTime * 5 + ai * 0.8) * 0.45;
            ctx.save();
            ctx.globalAlpha = arrowIntensity * pulse * 0.92;
            ctx.fillStyle = arrowIntensity > 0.65 ? '#ff3300' : '#ffcc00';
            ctx.font = `bold ${Math.round(as)}px sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText(arrowDir > 0 ? '➡' : '⬅', ax, ay + as * 0.3);
            ctx.globalAlpha = 1;
            ctx.restore();
          }
        }
      }

      // Bottom depth shadow
      const bottomFade = ctx.createLinearGradient(ox, oy + h * 0.82, ox, oy + h);
      bottomFade.addColorStop(0, 'rgba(0,0,0,0)');
      bottomFade.addColorStop(1, 'rgba(0,0,0,0.22)');
      ctx.fillStyle = bottomFade;
      ctx.fillRect(ox, oy + h * 0.82, w, h * 0.18);

      void h;
    }

    // ─────────────────────────────────────────────────────────────
    // DRAW: Finish Line
    // ─────────────────────────────────────────────────────────────
    function drawFinishLine(ox: number, oy: number) {
      if (!finishLineVisible || finishLineDepth <= 0.02) return;
      const depth = finishLineDepth;
      const w = cw();
      const horizonY = oy + ch() * 0.44;
      const groundY = oy + ch() * 0.98;
      const vanishX = ox + getTrackCenterX();
      const trackHalfNear = w * 0.48;
      const trackHalfFar = w * 0.038;
      const lineY = horizonY + (groundY - horizonY) * Math.pow(depth, 0.68);
      const halfW = trackHalfFar + (trackHalfNear - trackHalfFar) * depth;
      const curveShift = trackCurveOffset * w * 0.28 * depth;
      const sc = perspScale(depth);
      const leftX = vanishX - halfW + curveShift;
      const rightX = vanishX + halfW + curveShift;
      const lineH = Math.max(6, sc * 60);
      const squareW = Math.max(4, (rightX - leftX) / 8);
      const rows = 2;
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < 8; col++) {
          const isBlack = (row + col) % 2 === 0;
          ctx.fillStyle = isBlack ? '#111' : '#fff';
          ctx.fillRect(leftX + col * squareW, lineY - lineH + row * (lineH / rows), squareW, lineH / rows);
        }
      }
      ctx.strokeStyle = '#ffd60a';
      ctx.lineWidth = Math.max(2, sc * 4);
      ctx.strokeRect(leftX, lineY - lineH, rightX - leftX, lineH);
      const poleH = lineH * 3.5;
      ctx.strokeStyle = '#ffd60a';
      ctx.lineWidth = Math.max(3, sc * 7);
      ctx.beginPath(); ctx.moveTo(leftX, lineY); ctx.lineTo(leftX, lineY - poleH); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(rightX, lineY); ctx.lineTo(rightX, lineY - poleH); ctx.stroke();
      if (depth > 0.25) {
        const fontSize = Math.max(10, sc * 80);
        ctx.save();
        ctx.font = `bold ${fontSize}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillStyle = '#ffd60a';
        ctx.shadowColor = '#ff6b00';
        ctx.shadowBlur = 12 * sc;
        ctx.fillText('🏁 FINISH', vanishX + curveShift, lineY - poleH - fontSize * 0.3);
        ctx.shadowBlur = 0;
        ctx.restore();
      }
    }

    // ─────────────────────────────────────────────────────────────
    // DRAW: Gap
    // ─────────────────────────────────────────────────────────────
    function drawGap(ox: number, oy: number, depth: number) {
      const w = cw();
      const horizonY = oy + ch() * 0.44;
      const vanishX = ox + getTrackCenterX();
      const trackHalfNear = w * 0.48;
      const trackHalfFar = w * 0.038;
      const gapY = horizonY + (oy + ch() * 0.98 - horizonY) * Math.pow(depth, 0.68);
      const gapHalfW = trackHalfFar + (trackHalfNear - trackHalfFar) * depth;
      const gapH = Math.max(10, depth * 44);
      const curveShift = trackCurveOffset * w * 0.10 * depth;
      if (depth > 0.45 && depth < 0.85) {
        const warnAlpha = Math.min(0.8, (depth - 0.45) * 2.5);
        ctx.strokeStyle = 'rgba(255,200,0,' + warnAlpha + ')';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 6]);
        ctx.beginPath();
        ctx.moveTo(vanishX - gapHalfW * 0.9 + curveShift, gapY - gapH * 1.5);
        ctx.lineTo(vanishX + gapHalfW * 0.9 + curveShift, gapY - gapH * 1.5);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      const gapGrad = ctx.createLinearGradient(vanishX - gapHalfW, gapY - gapH, vanishX - gapHalfW, gapY + gapH);
      gapGrad.addColorStop(0, 'rgba(20,12,5,0.0)');
      gapGrad.addColorStop(0.25, 'rgba(8,4,1,0.96)');
      gapGrad.addColorStop(0.75, 'rgba(8,4,1,0.96)');
      gapGrad.addColorStop(1, 'rgba(20,12,5,0.0)');
      ctx.fillStyle = gapGrad;
      ctx.beginPath();
      ctx.moveTo(vanishX - gapHalfW + curveShift, gapY - gapH * 0.5);
      ctx.lineTo(vanishX + gapHalfW + curveShift, gapY - gapH * 0.5);
      ctx.lineTo(vanishX + gapHalfW + curveShift, gapY + gapH * 0.5);
      ctx.lineTo(vanishX - gapHalfW + curveShift, gapY + gapH * 0.5);
      ctx.closePath();
      ctx.fill();
    }

    // ─────────────────────────────────────────────────────────────
    // DRAW: ITALIAN Obstacles — location-appropriate types
    // Type 0: Grazing cow (wanders on the road)
    // Type 1: Pasta cart (wooden cart blocking path)
    // Type 2: Cobblestone gap / pothole
    // Type 3: Gap (ravine)
    // Type 4: Wine barrel stack
    // Type 5: Stone fountain
    // Type 6: Hay bale
    // Type 7: Vespa scooter
    // ─────────────────────────────────────────────────────────────
    function drawObstacles(ox: number, oy: number) {
      const sorted = [...obstacles.filter(o => o.active && o.depth > 0.05 && o.depth <= 1.05)];
      sorted.sort((a, b) => a.depth - b.depth);

      for (const o of sorted) {
        if (o.type === 3) { drawGap(ox, oy, o.depth); continue; }

        const sx = ox + perspX(o.laneX, o.depth);
        const sy = oy + perspY(o.depth);
        const sc = perspScale(o.depth);
        const anim = o.animPhase + runTime;
        const widthMult = o.fullWidth ? 1.8 : 1.0;

        // Warning indicator
        if (o.depth > 0.52 && o.depth < 0.86) {
          const warnProgress = (o.depth - 0.52) / (0.86 - 0.52);
          const warnAlpha = Math.min(1.0, warnProgress * 1.8);
          const pulse = 1 + Math.sin(runTime * 8) * 0.15;
          const warnSize = Math.round(22 + warnProgress * 18) * pulse;
          ctx.save();
          ctx.globalAlpha = warnAlpha;
          const pillW = warnSize * 2.2;
          const pillH = warnSize * 1.1;
          const pillX = sx - pillW / 2;
          const pillY = sy - 85 * sc * widthMult - pillH;
          ctx.fillStyle = 'rgba(255,40,40,0.85)';
          ctx.beginPath();
          ctx.roundRect(pillX, pillY, pillW, pillH, pillH / 2);
          ctx.fill();
          ctx.fillStyle = '#ffffff';
          ctx.font = `bold ${Math.round(warnSize * 0.7)}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.fillText('⚠ JUMP!', sx, pillY + pillH * 0.72);
          ctx.globalAlpha = 1;
          ctx.restore();
        }

        ctx.save();
        ctx.translate(sx, sy);
        ctx.scale(sc * widthMult, sc);

        if (o.type === 0) {
          // ── GRAZING COW — wanders slowly across the road
          const cowSway = Math.sin(anim * 1.2) * 10;
          ctx.save();
          ctx.translate(cowSway, 0);
          // Shadow
          ctx.fillStyle = 'rgba(0,0,0,0.20)';
          ctx.beginPath(); ctx.ellipse(0, 12, 52, 10, 0, 0, Math.PI * 2); ctx.fill();
          // Body
          const bodyGrad = ctx.createRadialGradient(-5, -24, 2, 0, -20, 46);
          bodyGrad.addColorStop(0, '#f8f4ee');
          bodyGrad.addColorStop(0.6, '#e0d8c8');
          bodyGrad.addColorStop(1, '#c8bfaa');
          ctx.fillStyle = bodyGrad;
          ctx.beginPath(); ctx.ellipse(0, -22, 44, 26, 0, 0, Math.PI * 2); ctx.fill();
          // Black patches
          ctx.fillStyle = '#2a2a2a';
          ctx.beginPath(); ctx.ellipse(-18, -32, 16, 12, 0.4, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.ellipse(18, -18, 14, 10, -0.3, 0, Math.PI * 2); ctx.fill();
          // Head
          ctx.fillStyle = '#f0ece0';
          ctx.beginPath(); ctx.ellipse(46, -32, 22, 17, 0.15, 0, Math.PI * 2); ctx.fill();
          // Nose
          ctx.fillStyle = '#e8a090';
          ctx.beginPath(); ctx.ellipse(58, -30, 8, 6, 0, 0, Math.PI * 2); ctx.fill();
          // Eye
          ctx.fillStyle = '#222';
          ctx.beginPath(); ctx.arc(50, -36, 3, 0, Math.PI * 2); ctx.fill();
          // Horns
          ctx.strokeStyle = '#c8a060'; ctx.lineWidth = 4; ctx.lineCap = 'round';
          ctx.beginPath(); ctx.moveTo(44, -46); ctx.lineTo(38, -58); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(52, -46); ctx.lineTo(58, -58); ctx.stroke();
          // Tail
          ctx.strokeStyle = '#c8a060'; ctx.lineWidth = 5;
          ctx.beginPath();
          ctx.moveTo(-46, -22);
          ctx.bezierCurveTo(-58, -12, -62, -2 + Math.sin(anim * 3.5) * 12, -54, 8);
          ctx.stroke(); ctx.lineCap = 'butt';
          // Legs
          ctx.fillStyle = '#e8e0d0';
          for (let li = 0; li < 4; li++) { ctx.beginPath(); ctx.roundRect(-36 + li * 22, -6, 14, 26, 4); ctx.fill(); }
          // Bell
          ctx.fillStyle = '#d4a820';
          ctx.beginPath(); ctx.roundRect(-4, -12, 8, 10, 2); ctx.fill();
          ctx.restore();
          ctx.font = `${Math.round(22 / widthMult)}px serif`;
          ctx.textAlign = 'center';
          ctx.fillText('🐄', 0, -62);

        } else if (o.type === 1) {
          // ── PASTA CART — wooden market cart with pasta hanging
          ctx.fillStyle = 'rgba(0,0,0,0.22)';
          ctx.beginPath(); ctx.ellipse(0, 10, 58, 10, 0, 0, Math.PI * 2); ctx.fill();
          // Cart body
          const cartGrad = ctx.createLinearGradient(-52, -38, 52, 0);
          cartGrad.addColorStop(0, '#c8902a');
          cartGrad.addColorStop(0.5, '#a87020');
          cartGrad.addColorStop(1, '#8a5a18');
          ctx.fillStyle = cartGrad;
          ctx.beginPath(); ctx.roundRect(-52, -38, 104, 48, 6); ctx.fill();
          // Wood planks
          ctx.strokeStyle = 'rgba(60,35,10,0.4)'; ctx.lineWidth = 2;
          for (let pi = 0; pi < 5; pi++) {
            ctx.beginPath(); ctx.moveTo(-52, -28 + pi * 9); ctx.lineTo(52, -28 + pi * 9); ctx.stroke();
          }
          // Wheels
          ctx.fillStyle = '#6a4010';
          ctx.beginPath(); ctx.arc(-36, 10, 18, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(36, 10, 18, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = '#c8902a'; ctx.lineWidth = 3;
          ctx.beginPath(); ctx.arc(-36, 10, 18, 0, Math.PI * 2); ctx.stroke();
          ctx.beginPath(); ctx.arc(36, 10, 18, 0, Math.PI * 2); ctx.stroke();
          // Spokes
          for (let sp = 0; sp < 6; sp++) {
            const ang = (sp / 6) * Math.PI * 2 + anim * 2;
            ctx.beginPath(); ctx.moveTo(-36, 10); ctx.lineTo(-36 + Math.cos(ang) * 16, 10 + Math.sin(ang) * 16); ctx.stroke();
            ctx.beginPath(); ctx.moveTo(36, 10); ctx.lineTo(36 + Math.cos(ang) * 16, 10 + Math.sin(ang) * 16); ctx.stroke();
          }
          // Hanging pasta strands
          ctx.strokeStyle = '#f0d890'; ctx.lineWidth = 2;
          for (let pa = 0; pa < 8; pa++) {
            const px = -40 + pa * 11;
            const swing = Math.sin(anim * 2 + pa * 0.5) * 4;
            ctx.beginPath();
            ctx.moveTo(px, -38);
            ctx.bezierCurveTo(px + swing, -28, px + swing * 0.5, -18, px + swing * 0.8, -8);
            ctx.stroke();
          }
          // Sign
          ctx.fillStyle = '#e8c060';
          ctx.beginPath(); ctx.roundRect(-28, -58, 56, 20, 4); ctx.fill();
          ctx.fillStyle = '#4a2a08';
          ctx.font = 'bold 11px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('PASTA!', 0, -43);
          ctx.font = `${Math.round(18 / widthMult)}px serif`;
          ctx.fillText('🍝', 0, -68);

        } else if (o.type === 2) {
          // ── COBBLESTONE GAP — sunken pothole section
          ctx.fillStyle = 'rgba(0,0,0,0.22)';
          ctx.beginPath(); ctx.ellipse(0, 8, 52, 8, 0, 0, Math.PI * 2); ctx.fill();
          // Pit
          const pitGrad = ctx.createLinearGradient(0, -5, 0, 20);
          pitGrad.addColorStop(0, '#2a1a0a');
          pitGrad.addColorStop(1, '#0a0500');
          ctx.fillStyle = pitGrad;
          ctx.beginPath(); ctx.roundRect(-50, -5, 100, 25, 4); ctx.fill();
          // Broken cobblestones around edge
          ctx.fillStyle = '#b0a080';
          for (let ci = 0; ci < 10; ci++) {
            const angle = (ci / 10) * Math.PI * 2;
            const cx2 = Math.cos(angle) * 48;
            const cy2 = Math.sin(angle) * 12;
            ctx.beginPath();
            ctx.roundRect(cx2 - 7, cy2 - 5, 12, 9, 2);
            ctx.fill();
          }
          ctx.fillStyle = '#888070';
          for (let ci = 0; ci < 6; ci++) {
            const angle = (ci / 6) * Math.PI * 2 + 0.3;
            const cx2 = Math.cos(angle) * 35;
            const cy2 = Math.sin(angle) * 9;
            ctx.beginPath();
            ctx.roundRect(cx2 - 5, cy2 - 4, 9, 7, 2);
            ctx.fill();
          }
          ctx.font = `${Math.round(18 / widthMult)}px serif`;
          ctx.textAlign = 'center';
          ctx.fillText('⚠️ GAP!', 0, -22);

        } else if (o.type === 4) {
          // ── WINE BARREL STACK — classic Italian cantina barrels
          const rollRot = Math.sin(anim * 1.5) * 0.08;
          ctx.save();
          ctx.rotate(rollRot);
          ctx.fillStyle = 'rgba(0,0,0,0.25)';
          ctx.beginPath(); ctx.ellipse(0, 14, 50, 10, 0, 0, Math.PI * 2); ctx.fill();
          // Bottom barrel
          const barrelGrad = ctx.createLinearGradient(-32, -20, 32, 20);
          barrelGrad.addColorStop(0, '#8B4513');
          barrelGrad.addColorStop(0.4, '#6B3010');
          barrelGrad.addColorStop(1, '#9B5523');
          ctx.fillStyle = barrelGrad;
          ctx.beginPath(); ctx.roundRect(-32, -20, 64, 34, 16); ctx.fill();
          // Metal hoops
          ctx.strokeStyle = '#c8a040'; ctx.lineWidth = 4;
          ctx.beginPath(); ctx.roundRect(-32, -20, 64, 34, 16); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(-32, -4); ctx.lineTo(32, -4); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(-32, 8); ctx.lineTo(32, 8); ctx.stroke();
          // Top barrel (smaller, stacked)
          ctx.fillStyle = barrelGrad;
          ctx.beginPath(); ctx.roundRect(-22, -50, 44, 30, 12); ctx.fill();
          ctx.strokeStyle = '#c8a040'; ctx.lineWidth = 3;
          ctx.beginPath(); ctx.roundRect(-22, -50, 44, 30, 12); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(-22, -36); ctx.lineTo(22, -36); ctx.stroke();
          // Wine drip
          ctx.fillStyle = 'rgba(140,20,20,0.7)';
          ctx.beginPath();
          ctx.moveTo(8, -20);
          ctx.bezierCurveTo(10, -10, 9, 0, 8, 8);
          ctx.lineTo(12, 8);
          ctx.bezierCurveTo(13, 0, 14, -10, 12, -20);
          ctx.closePath(); ctx.fill();
          ctx.restore();
          ctx.font = `${Math.round(20 / widthMult)}px serif`;
          ctx.textAlign = 'center';
          ctx.fillText('🍷', 0, -62);

        } else if (o.type === 5) {
          // ── STONE FOUNTAIN — classic Italian piazza fountain
          ctx.fillStyle = 'rgba(0,0,0,0.28)';
          ctx.beginPath(); ctx.ellipse(0, 12, 56, 12, 0, 0, Math.PI * 2); ctx.fill();
          // Basin
          const basinGrad = ctx.createRadialGradient(0, 0, 5, 0, 0, 52);
          basinGrad.addColorStop(0, '#d0c8b8');
          basinGrad.addColorStop(0.6, '#b8b0a0');
          basinGrad.addColorStop(1, '#a0988a');
          ctx.fillStyle = basinGrad;
          ctx.beginPath(); ctx.ellipse(0, 0, 52, 14, 0, 0, Math.PI * 2); ctx.fill();
          // Basin rim
          ctx.strokeStyle = '#c0b8a8'; ctx.lineWidth = 5;
          ctx.beginPath(); ctx.ellipse(0, 0, 52, 14, 0, 0, Math.PI * 2); ctx.stroke();
          // Water in basin
          ctx.fillStyle = 'rgba(80,160,220,0.55)';
          ctx.beginPath(); ctx.ellipse(0, 2, 44, 10, 0, 0, Math.PI * 2); ctx.fill();
          // Center column
          ctx.fillStyle = '#c8c0b0';
          ctx.beginPath(); ctx.roundRect(-8, -48, 16, 48, 4); ctx.fill();
          // Top bowl
          ctx.fillStyle = '#d0c8b8';
          ctx.beginPath(); ctx.ellipse(0, -48, 28, 8, 0, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = '#b8b0a0'; ctx.lineWidth = 3;
          ctx.beginPath(); ctx.ellipse(0, -48, 28, 8, 0, 0, Math.PI * 2); ctx.stroke();
          // Water jet
          const jetH = 20 + Math.sin(anim * 4) * 4;
          ctx.strokeStyle = 'rgba(100,180,240,0.7)'; ctx.lineWidth = 3;
          ctx.beginPath(); ctx.moveTo(0, -56); ctx.lineTo(0, -56 - jetH); ctx.stroke();
          // Water droplets
          ctx.fillStyle = 'rgba(100,180,240,0.6)';
          for (let di = 0; di < 5; di++) {
            const da = (di / 5) * Math.PI * 2 + anim * 2;
            const dr = 12 + Math.sin(anim * 3 + di) * 4;
            ctx.beginPath(); ctx.arc(Math.cos(da) * dr, -48 - 8 + Math.sin(da) * 4, 2.5, 0, Math.PI * 2); ctx.fill();
          }
          ctx.font = `${Math.round(18 / widthMult)}px serif`;
          ctx.textAlign = 'center';
          ctx.fillText('⛲', 0, -82);

        } else if (o.type === 6) {
          // ── HAY BALE — round Italian farm bale
          const rollAnim = anim * 0.8;
          ctx.save();
          ctx.rotate(Math.sin(rollAnim) * 0.05);
          ctx.fillStyle = 'rgba(0,0,0,0.22)';
          ctx.beginPath(); ctx.ellipse(0, 12, 48, 10, 0, 0, Math.PI * 2); ctx.fill();
          // Bale body
          const hayGrad = ctx.createRadialGradient(-10, -20, 5, 0, -10, 46);
          hayGrad.addColorStop(0, '#f0d870');
          hayGrad.addColorStop(0.5, '#d4b840');
          hayGrad.addColorStop(1, '#b89820');
          ctx.fillStyle = hayGrad;
          ctx.beginPath(); ctx.arc(0, -8, 44, 0, Math.PI * 2); ctx.fill();
          // Hay texture rings
          ctx.strokeStyle = 'rgba(160,120,20,0.4)'; ctx.lineWidth = 2;
          for (let ri = 1; ri <= 4; ri++) {
            ctx.beginPath(); ctx.arc(0, -8, ri * 10, 0, Math.PI * 2); ctx.stroke();
          }
          // Twine bands
          ctx.strokeStyle = '#8a6010'; ctx.lineWidth = 4;
          ctx.beginPath(); ctx.arc(0, -8, 44, -0.3, Math.PI + 0.3); ctx.stroke();
          ctx.beginPath(); ctx.arc(0, -8, 44, Math.PI - 0.3, Math.PI * 2 + 0.3); ctx.stroke();
          ctx.restore();
          ctx.font = `${Math.round(20 / widthMult)}px serif`;
          ctx.textAlign = 'center';
          ctx.fillText('🌾', 0, -62);

        } else if (o.type === 7) {
          // ── VESPA SCOOTER — parked Italian scooter blocking path
          const scootSway = Math.sin(anim * 1.0) * 5;
          ctx.save();
          ctx.translate(scootSway, 0);
          ctx.fillStyle = 'rgba(0,0,0,0.25)';
          ctx.beginPath(); ctx.ellipse(0, 10, 44, 8, 0, 0, Math.PI * 2); ctx.fill();
          // Body
          ctx.fillStyle = '#cc2222';
          ctx.beginPath();
          ctx.moveTo(-38, 0);
          ctx.bezierCurveTo(-40, -18, -20, -38, 0, -40);
          ctx.bezierCurveTo(20, -42, 36, -28, 38, -10);
          ctx.bezierCurveTo(40, 0, 30, 8, 0, 8);
          ctx.bezierCurveTo(-30, 8, -36, 4, -38, 0);
          ctx.closePath(); ctx.fill();
          // Windshield
          ctx.fillStyle = 'rgba(180,220,255,0.6)';
          ctx.beginPath(); ctx.roundRect(-10, -42, 20, 16, 4); ctx.fill();
          // Wheels
          ctx.fillStyle = '#222';
          ctx.beginPath(); ctx.arc(-28, 8, 14, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(28, 8, 14, 0, Math.PI * 2); ctx.fill();
          ctx.strokeStyle = '#888'; ctx.lineWidth = 3;
          ctx.beginPath(); ctx.arc(-28, 8, 14, 0, Math.PI * 2); ctx.stroke();
          ctx.beginPath(); ctx.arc(28, 8, 14, 0, Math.PI * 2); ctx.stroke();
          // Handlebar
          ctx.strokeStyle = '#888'; ctx.lineWidth = 5; ctx.lineCap = 'round';
          ctx.beginPath(); ctx.moveTo(-18, -38); ctx.lineTo(18, -38); ctx.stroke();
          ctx.lineCap = 'butt';
          ctx.restore();
          ctx.font = `${Math.round(20 / widthMult)}px serif`;
          ctx.textAlign = 'center';
          ctx.fillText('🛵', 0, -62);

        } else if (o.type === 8) {
          // ── RICE BOWL — Japan Mt Fuji obstacle
          const bowlSway = Math.sin(anim * 1.4) * 6;
          ctx.save();
          ctx.translate(bowlSway, 0);
          // Shadow
          ctx.fillStyle = 'rgba(0,0,0,0.22)';
          ctx.beginPath(); ctx.ellipse(0, 14, 46, 9, 0, 0, Math.PI * 2); ctx.fill();
          // Bowl base
          const bowlGrad = ctx.createLinearGradient(-40, -10, 40, 20);
          bowlGrad.addColorStop(0, '#f5f0e8');
          bowlGrad.addColorStop(0.5, '#e8e0d0');
          bowlGrad.addColorStop(1, '#d0c8b8');
          ctx.fillStyle = bowlGrad;
          ctx.beginPath();
          ctx.moveTo(-40, 0);
          ctx.bezierCurveTo(-42, 10, -30, 20, 0, 20);
          ctx.bezierCurveTo(30, 20, 42, 10, 40, 0);
          ctx.closePath(); ctx.fill();
          // Bowl rim
          ctx.strokeStyle = '#c0b8a8'; ctx.lineWidth = 3;
          ctx.beginPath(); ctx.ellipse(0, 0, 40, 10, 0, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = '#e8e0d0';
          ctx.beginPath(); ctx.ellipse(0, 0, 40, 10, 0, 0, Math.PI * 2); ctx.fill();
          ctx.stroke();
          // Rice mound
          ctx.fillStyle = '#fffef8';
          ctx.beginPath(); ctx.ellipse(0, -4, 32, 14, 0, 0, Math.PI * 2); ctx.fill();
          // Rice texture dots
          ctx.fillStyle = 'rgba(220,215,200,0.8)';
          for (let ri = 0; ri < 12; ri++) {
            const rx = Math.cos((ri / 12) * Math.PI * 2) * 18;
            const ry = Math.sin((ri / 12) * Math.PI * 2) * 7 - 4;
            ctx.beginPath(); ctx.ellipse(rx, ry, 3, 2, 0, 0, Math.PI * 2); ctx.fill();
          }
          // Chopsticks
          ctx.strokeStyle = '#8B6914'; ctx.lineWidth = 3; ctx.lineCap = 'round';
          ctx.beginPath(); ctx.moveTo(-8, -18); ctx.lineTo(-4, 8); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(8, -18); ctx.lineTo(4, 8); ctx.stroke();
          ctx.lineCap = 'butt';
          ctx.restore();
          ctx.font = `${Math.round(22 / widthMult)}px serif`;
          ctx.textAlign = 'center';
          ctx.fillText('🍚', 0, -52);

        } else if (o.type === 9) {
          // ── DEER — Japan Mt Fuji deer crossing the road
          const deerSway = Math.sin(anim * 1.1) * 8;
          ctx.save();
          ctx.translate(deerSway, 0);
          // Shadow
          ctx.fillStyle = 'rgba(0,0,0,0.20)';
          ctx.beginPath(); ctx.ellipse(0, 14, 44, 9, 0, 0, Math.PI * 2); ctx.fill();
          // Body
          const deerBodyGrad = ctx.createRadialGradient(-5, -22, 3, 0, -18, 40);
          deerBodyGrad.addColorStop(0, '#d4884a');
          deerBodyGrad.addColorStop(0.5, '#b86830');
          deerBodyGrad.addColorStop(1, '#8a4a18');
          ctx.fillStyle = deerBodyGrad;
          ctx.beginPath(); ctx.ellipse(0, -18, 38, 22, 0, 0, Math.PI * 2); ctx.fill();
          // White belly patch
          ctx.fillStyle = '#f8f0e0';
          ctx.beginPath(); ctx.ellipse(0, -10, 22, 12, 0, 0, Math.PI * 2); ctx.fill();
          // Neck
          ctx.fillStyle = '#c07838';
          ctx.beginPath(); ctx.roundRect(28, -38, 18, 28, 6); ctx.fill();
          // Head
          ctx.fillStyle = '#d4884a';
          ctx.beginPath(); ctx.ellipse(44, -44, 18, 14, 0.2, 0, Math.PI * 2); ctx.fill();
          // Nose
          ctx.fillStyle = '#e8a090';
          ctx.beginPath(); ctx.ellipse(56, -42, 7, 5, 0, 0, Math.PI * 2); ctx.fill();
          // Eye
          ctx.fillStyle = '#1a1a1a';
          ctx.beginPath(); ctx.arc(48, -48, 3, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = 'rgba(255,255,255,0.6)';
          ctx.beginPath(); ctx.arc(49, -49, 1.2, 0, Math.PI * 2); ctx.fill();
          // Ears
          ctx.fillStyle = '#d4884a';
          ctx.beginPath(); ctx.ellipse(38, -56, 6, 12, -0.3, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.ellipse(50, -56, 5, 11, 0.2, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = '#f0a0a0';
          ctx.beginPath(); ctx.ellipse(38, -56, 3, 7, -0.3, 0, Math.PI * 2); ctx.fill();
          // Antlers
          ctx.strokeStyle = '#6a3a10'; ctx.lineWidth = 4; ctx.lineCap = 'round';
          ctx.beginPath(); ctx.moveTo(36, -58); ctx.lineTo(28, -76); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(28, -70); ctx.lineTo(20, -78); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(28, -70); ctx.lineTo(36, -80); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(48, -58); ctx.lineTo(54, -76); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(54, -70); ctx.lineTo(46, -80); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(54, -70); ctx.lineTo(62, -78); ctx.stroke();
          ctx.lineCap = 'butt';
          // Tail
          ctx.fillStyle = '#f8f0e0';
          ctx.beginPath(); ctx.ellipse(-38, -22, 8, 10, 0.2, 0, Math.PI * 2); ctx.fill();
          // Legs
          ctx.fillStyle = '#b86830';
          for (let li = 0; li < 4; li++) {
            const legX = -28 + li * 18;
            const legBob = Math.sin(anim * 3 + li * 0.8) * 3;
            ctx.beginPath(); ctx.roundRect(legX, -4 + legBob, 10, 22, 3); ctx.fill();
          }
          ctx.restore();
          ctx.font = `${Math.round(22 / widthMult)}px serif`;
          ctx.textAlign = 'center';
          ctx.fillText('🦌', 0, -92);

        } else if (o.type === 10) {
          // ── MUSHROOM — Rainbow Road obstacle
          const mushBob = Math.sin(anim * 2.2) * 5;
          ctx.save();
          ctx.translate(0, mushBob);
          // Shadow
          ctx.fillStyle = 'rgba(0,0,0,0.18)';
          ctx.beginPath(); ctx.ellipse(0, 16 - mushBob, 38, 7, 0, 0, Math.PI * 2); ctx.fill();
          // Stem
          const stemGrad = ctx.createLinearGradient(-14, -10, 14, 20);
          stemGrad.addColorStop(0, '#f5f0e0');
          stemGrad.addColorStop(1, '#d8d0b8');
          ctx.fillStyle = stemGrad;
          ctx.beginPath(); ctx.roundRect(-14, -10, 28, 30, 4); ctx.fill();
          // Stem spots
          ctx.fillStyle = 'rgba(200,190,160,0.6)';
          ctx.beginPath(); ctx.ellipse(-4, 2, 5, 4, 0, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.ellipse(6, 12, 4, 3, 0, 0, Math.PI * 2); ctx.fill();
          // Cap
          const capGrad = ctx.createRadialGradient(-8, -38, 4, 0, -32, 38);
          capGrad.addColorStop(0, '#ff4444');
          capGrad.addColorStop(0.5, '#cc2222');
          capGrad.addColorStop(1, '#881111');
          ctx.fillStyle = capGrad;
          ctx.beginPath();
          ctx.arc(0, -28, 38, Math.PI, 0);
          ctx.bezierCurveTo(38, -10, 20, -8, 0, -8);
          ctx.bezierCurveTo(-20, -8, -38, -10, -38, -28);
          ctx.closePath(); ctx.fill();
          // White spots on cap
          ctx.fillStyle = 'rgba(255,255,255,0.9)';
          const spotPositions = [[-14, -38, 7], [10, -44, 9], [22, -28, 6], [-22, -28, 5], [0, -52, 5]];
          for (const [spx, spy, spr] of spotPositions) {
            ctx.beginPath(); ctx.arc(spx, spy, spr, 0, Math.PI * 2); ctx.fill();
          }
          // Rainbow glow
          ctx.save();
          ctx.globalAlpha = 0.4 + Math.sin(anim * 3) * 0.2;
          ctx.shadowColor = '#ff4444';
          ctx.shadowBlur = 18;
          ctx.strokeStyle = '#ff8888';
          ctx.lineWidth = 2;
          ctx.beginPath(); ctx.arc(0, -28, 40, Math.PI, 0); ctx.stroke();
          ctx.shadowBlur = 0;
          ctx.restore();
          ctx.restore();
          ctx.font = `${Math.round(22 / widthMult)}px serif`;
          ctx.textAlign = 'center';
          ctx.fillText('🍄', 0, -76);

        } else if (o.type === 11) {
          // ── STAR — Rainbow Road spinning star obstacle
          const starSpin = anim * 2.5;
          ctx.save();
          ctx.rotate(starSpin);
          // Shadow (counter-rotate so it stays flat)
          ctx.save();
          ctx.rotate(-starSpin);
          ctx.fillStyle = 'rgba(0,0,0,0.18)';
          ctx.beginPath(); ctx.ellipse(0, 30, 36, 7, 0, 0, Math.PI * 2); ctx.fill();
          ctx.restore();
          // Star glow
          const glowAlpha = 0.5 + Math.sin(anim * 4) * 0.3;
          ctx.save();
          ctx.globalAlpha = glowAlpha;
          ctx.shadowColor = '#ffd700';
          ctx.shadowBlur = 28;
          // Star shape
          const starGrad = ctx.createRadialGradient(0, 0, 2, 0, 0, 36);
          starGrad.addColorStop(0, '#fff8a0');
          starGrad.addColorStop(0.4, '#ffd700');
          starGrad.addColorStop(1, '#ff8800');
          ctx.fillStyle = starGrad;
          ctx.beginPath();
          for (let si = 0; si < 10; si++) {
            const angle = (si / 10) * Math.PI * 2 - Math.PI / 2;
            const r2 = si % 2 === 0 ? 36 : 16;
            const px = Math.cos(angle) * r2;
            const py = Math.sin(angle) * r2;
            if (si === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.closePath(); ctx.fill();
          ctx.shadowBlur = 0;
          ctx.restore();
          // Sparkle dots
          ctx.fillStyle = 'rgba(255,255,200,0.9)';
          for (let sp = 0; sp < 6; sp++) {
            const sa = (sp / 6) * Math.PI * 2 + anim * 5;
            ctx.beginPath(); ctx.arc(Math.cos(sa) * 44, Math.sin(sa) * 44, 3, 0, Math.PI * 2); ctx.fill();
          }
          ctx.restore();
          ctx.font = `${Math.round(22 / widthMult)}px serif`;
          ctx.textAlign = 'center';
          ctx.fillText('⭐', 0, -58);
        }

        ctx.restore();
      }
    }

    // ─────────────────────────────────────────────────────────────
    // DRAW: EXTREME Ramps — 6 dramatic types
    // ─────────────────────────────────────────────────────────────
    function drawRamps(ox: number, oy: number) {
      for (const r of ramps) {
        if (!r.active || r.depth <= 0.05 || r.depth > 1.05) continue;
        const sx = ox + perspX(r.laneX, r.depth);
        const sy = oy + perspY(r.depth);
        const sc = perspScale(r.depth);
        const glowPulse = 0.7 + Math.sin(r.glowPhase + runTime * 4) * 0.3;

        // Ramp approach indicator
        if (r.depth > 0.48 && r.depth < 0.90) {
          const glowProgress = (r.depth - 0.48) / (0.90 - 0.48);
          const glowAlpha = Math.min(0.9, glowProgress * 1.8);
          const pulse = 1 + Math.sin(runTime * 7) * 0.18;
          ctx.save();
          ctx.globalAlpha = glowAlpha;
          const rampLabels = ['↑ RAMP', '🚀 MEGA!', '⚡ KICKER!', '🌊 HALFPIPE!', '🔥 LAUNCH!', '💥 DOUBLE!'];
          const rampColors = ['#00ff88', '#ff9f43', '#00e5ff', '#c084fc', '#ff4400', '#ffd60a'];
          ctx.fillStyle = rampColors[r.rampType % 6];
          ctx.font = `bold ${Math.round((r.rampType >= 1 ? 20 : 15) * sc * 3.5 * pulse)}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.shadowColor = rampColors[r.rampType % 6];
          ctx.shadowBlur = 15 * glowProgress;
          ctx.fillText(rampLabels[r.rampType % 6], sx, sy - 80 * sc);
          ctx.shadowBlur = 0;
          ctx.globalAlpha = 1;
          ctx.restore();
        }

        ctx.save();
        ctx.translate(sx, sy);
        ctx.scale(sc, sc);

        if (r.rampType === 0) {
          // ── STANDARD RAMP — clean dirt ramp
          const rampW = 65, rampH = 28;
          ctx.fillStyle = 'rgba(0,0,0,0.25)';
          ctx.beginPath(); ctx.ellipse(0, 5, rampW * 0.65, 7, 0, 0, Math.PI * 2); ctx.fill();
          const rGrad = ctx.createLinearGradient(-rampW / 2, -rampH, rampW / 2, 0);
          rGrad.addColorStop(0, '#d4c8b0');
          rGrad.addColorStop(0.5, '#b0a890');
          rGrad.addColorStop(1, '#807860');
          ctx.fillStyle = rGrad;
          ctx.beginPath();
          ctx.moveTo(-rampW / 2, 0);
          ctx.lineTo(rampW / 2, 0);
          ctx.lineTo(rampW / 2, -rampH);
          ctx.bezierCurveTo(rampW / 2, -rampH - 8, -rampW / 2 + 10, -rampH * 0.25, -rampW / 2, 0);
          ctx.closePath(); ctx.fill();
          ctx.strokeStyle = 'rgba(255,252,245,0.6)'; ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(-rampW / 2, 0);
          ctx.bezierCurveTo(-rampW / 2 + 10, -rampH * 0.25, rampW / 2, -rampH - 8, rampW / 2, -rampH);
          ctx.stroke();

        } else if (r.rampType === 1) {
          // ── MEGA RAMP — massive orange ramp with launch platform
          const rampW = 110, rampH = 65;
          ctx.fillStyle = 'rgba(0,0,0,0.35)';
          ctx.beginPath(); ctx.ellipse(0, 8, rampW * 0.7, 12, 0, 0, Math.PI * 2); ctx.fill();
          const rGrad = ctx.createLinearGradient(-rampW / 2, -rampH, rampW / 2, 0);
          rGrad.addColorStop(0, '#ffb347');
          rGrad.addColorStop(0.4, '#e8902a');
          rGrad.addColorStop(1, '#c07020');
          ctx.fillStyle = rGrad;
          ctx.beginPath();
          ctx.moveTo(-rampW / 2, 0);
          ctx.lineTo(rampW / 2, 0);
          ctx.lineTo(rampW / 2, -rampH);
          ctx.bezierCurveTo(rampW / 2, -rampH - 18, -rampW / 2 + 18, -rampH * 0.22, -rampW / 2, 0);
          ctx.closePath(); ctx.fill();
          // Launch platform at top
          ctx.fillStyle = '#ffcc44';
          ctx.beginPath(); ctx.roundRect(rampW / 2 - 22, -rampH - 12, 22, 14, 3); ctx.fill();
          // Glow
          ctx.save();
          ctx.globalAlpha = glowPulse * 0.6;
          ctx.shadowColor = '#ff9f43';
          ctx.shadowBlur = 25;
          ctx.strokeStyle = '#ffcc44';
          ctx.lineWidth = 4;
          ctx.beginPath();
          ctx.moveTo(-rampW / 2, 0);
          ctx.bezierCurveTo(-rampW / 2 + 18, -rampH * 0.22, rampW / 2, -rampH - 18, rampW / 2, -rampH);
          ctx.stroke();
          ctx.shadowBlur = 0;
          ctx.restore();
          ctx.fillStyle = 'rgba(255,200,40,0.95)';
          ctx.font = '26px serif';
          ctx.textAlign = 'center';
          ctx.fillText('🚀', 0, -rampH - 20);

        } else if (r.rampType === 2) {
          // ── KICKER — short sharp pop ramp, neon blue
          const rampW = 50, rampH = 38;
          ctx.fillStyle = 'rgba(0,0,0,0.25)';
          ctx.beginPath(); ctx.ellipse(0, 5, rampW * 0.6, 6, 0, 0, Math.PI * 2); ctx.fill();
          const rGrad = ctx.createLinearGradient(-rampW / 2, -rampH, rampW / 2, 0);
          rGrad.addColorStop(0, '#00e5ff');
          rGrad.addColorStop(0.5, '#0088cc');
          rGrad.addColorStop(1, '#004488');
          ctx.fillStyle = rGrad;
          ctx.beginPath();
          ctx.moveTo(-rampW / 2, 0);
          ctx.lineTo(rampW / 2, 0);
          ctx.lineTo(rampW / 2, -rampH);
          ctx.lineTo(-rampW / 2 + 8, 0);
          ctx.closePath(); ctx.fill();
          // Neon edge
          ctx.save();
          ctx.globalAlpha = glowPulse;
          ctx.shadowColor = '#00e5ff';
          ctx.shadowBlur = 20;
          ctx.strokeStyle = '#00e5ff';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(-rampW / 2 + 8, 0);
          ctx.lineTo(rampW / 2, -rampH);
          ctx.stroke();
          ctx.shadowBlur = 0;
          ctx.restore();
          ctx.fillStyle = '#00e5ff';
          ctx.font = '18px serif';
          ctx.textAlign = 'center';
          ctx.fillText('⚡', 0, -rampH - 14);

        } else if (r.rampType === 3) {
          // ── HALFPIPE — curved bowl section
          const pipeW = 90, pipeH = 50;
          ctx.fillStyle = 'rgba(0,0,0,0.28)';
          ctx.beginPath(); ctx.ellipse(0, 8, pipeW * 0.65, 10, 0, 0, Math.PI * 2); ctx.fill();
          // Pipe walls
          const pipeGrad = ctx.createLinearGradient(-pipeW / 2, -pipeH, pipeW / 2, 0);
          pipeGrad.addColorStop(0, '#c084fc');
          pipeGrad.addColorStop(0.5, '#9050d0');
          pipeGrad.addColorStop(1, '#6030a0');
          ctx.fillStyle = pipeGrad;
          // Left wall
          ctx.beginPath();
          ctx.moveTo(-pipeW / 2, 0);
          ctx.bezierCurveTo(-pipeW / 2, -pipeH * 0.4, -pipeW / 2 + 15, -pipeH, -pipeW / 2 + 30, -pipeH);
          ctx.lineTo(-pipeW / 2 + 30, -pipeH + 10);
          ctx.bezierCurveTo(-pipeW / 2 + 15, -pipeH + 10, -pipeW / 2 + 10, -pipeH * 0.4, -pipeW / 2 + 10, 0);
          ctx.closePath(); ctx.fill();
          // Right wall
          ctx.beginPath();
          ctx.moveTo(pipeW / 2, 0);
          ctx.bezierCurveTo(pipeW / 2, -pipeH * 0.4, pipeW / 2 - 15, -pipeH, pipeW / 2 - 30, -pipeH);
          ctx.lineTo(pipeW / 2 - 30, -pipeH + 10);
          ctx.bezierCurveTo(pipeW / 2 - 15, -pipeH + 10, pipeW / 2 - 10, -pipeH * 0.4, pipeW / 2 - 10, 0);
          ctx.closePath(); ctx.fill();
          // Floor
          ctx.fillStyle = '#7040b0';
          ctx.beginPath(); ctx.roundRect(-pipeW / 2 + 10, -8, pipeW - 20, 8, 3); ctx.fill();
          // Neon lip
          ctx.save();
          ctx.globalAlpha = glowPulse;
          ctx.shadowColor = '#c084fc';
          ctx.shadowBlur = 18;
          ctx.strokeStyle = '#e0a0ff';
          ctx.lineWidth = 3;
          ctx.beginPath(); ctx.moveTo(-pipeW / 2 + 30, -pipeH); ctx.lineTo(pipeW / 2 - 30, -pipeH); ctx.stroke();
          ctx.shadowBlur = 0;
          ctx.restore();
          ctx.fillStyle = '#c084fc';
          ctx.font = '20px serif';
          ctx.textAlign = 'center';
          ctx.fillText('🌊', 0, -pipeH - 14);

        } else if (r.rampType === 4) {
          // ── LAUNCH PAD — flat pad with rocket boosters
          const padW = 80, padH = 18;
          ctx.fillStyle = 'rgba(0,0,0,0.30)';
          ctx.beginPath(); ctx.ellipse(0, 6, padW * 0.65, 8, 0, 0, Math.PI * 2); ctx.fill();
          // Pad surface
          const padGrad = ctx.createLinearGradient(-padW / 2, -padH, padW / 2, 0);
          padGrad.addColorStop(0, '#ff6644');
          padGrad.addColorStop(0.5, '#ff4422');
          padGrad.addColorStop(1, '#cc2200');
          ctx.fillStyle = padGrad;
          ctx.beginPath(); ctx.roundRect(-padW / 2, -padH, padW, padH, 5); ctx.fill();
          // Arrow markings
          ctx.fillStyle = '#ffcc00';
          ctx.font = 'bold 18px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('▲▲▲', 0, -padH * 0.3);
          // Booster flames
          ctx.save();
          ctx.globalAlpha = glowPulse;
          ctx.shadowColor = '#ff4400';
          ctx.shadowBlur = 22;
          for (let bi = 0; bi < 3; bi++) {
            const bx = -24 + bi * 24;
            ctx.fillStyle = bi % 2 === 0 ? '#ff6600' : '#ffcc00';
            ctx.beginPath(); ctx.arc(bx, -padH - 8 - Math.sin(runTime * 10 + bi) * 4, 6, 0, Math.PI * 2); ctx.fill();
          }
          ctx.shadowBlur = 0;
          ctx.restore();
          ctx.fillStyle = '#ff4400';
          ctx.font = '20px serif';
          ctx.textAlign = 'center';
          ctx.fillText('🔥', 0, -padH - 22);

        } else {
          // ── DOUBLE KICKER — two ramps side by side
          const rampW = 44, rampH = 42;
          for (let di = 0; di < 2; di++) {
            const dx = di === 0 ? -28 : 28;
            const dColor = di === 0 ? '#ffd60a' : '#ff6b00';
            ctx.fillStyle = 'rgba(0,0,0,0.20)';
            ctx.beginPath(); ctx.ellipse(dx, 5, rampW * 0.5, 5, 0, 0, Math.PI * 2); ctx.fill();
            const rGrad = ctx.createLinearGradient(dx - rampW / 2, -rampH, dx + rampW / 2, 0);
            rGrad.addColorStop(0, dColor);
            rGrad.addColorStop(1, '#884400');
            ctx.fillStyle = rGrad;
            ctx.beginPath();
            ctx.moveTo(dx - rampW / 2, 0);
            ctx.lineTo(dx + rampW / 2, 0);
            ctx.lineTo(dx + rampW / 2, -rampH);
            ctx.lineTo(dx - rampW / 2 + 6, 0);
            ctx.closePath(); ctx.fill();
            ctx.save();
            ctx.globalAlpha = glowPulse * 0.8;
            ctx.shadowColor = dColor;
            ctx.shadowBlur = 16;
            ctx.strokeStyle = dColor;
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(dx - rampW / 2 + 6, 0);
            ctx.lineTo(dx + rampW / 2, -rampH);
            ctx.stroke();
            ctx.shadowBlur = 0;
            ctx.restore();
          }
          ctx.fillStyle = '#ffd60a';
          ctx.font = '18px serif';
          ctx.textAlign = 'center';
          ctx.fillText('💥', 0, -rampH - 16);
        }

        ctx.restore();
      }
    }

    // ─────────────────────────────────────────────────────────────
    // DRAW: Particles + Score Popups
    // ─────────────────────────────────────────────────────────────
    function drawParticles(ox: number, oy: number) {
      for (const p of particles) {
        const alpha = p.life / p.maxLife;
        ctx.globalAlpha = Math.max(0, alpha);
        if (p.type === 'smoke') {
          ctx.fillStyle = p.color;
          ctx.beginPath();
          ctx.arc(ox + p.x, oy + p.y, p.size * alpha, 0, Math.PI * 2);
          ctx.fill();
        } else if (p.type === 'star') {
          ctx.save();
          ctx.translate(ox + p.x, oy + p.y);
          ctx.rotate(p.life * 3);
          ctx.fillStyle = p.color;
          ctx.beginPath();
          for (let si = 0; si < 5; si++) {
            const angle = (si * Math.PI * 2) / 5 - Math.PI / 2;
            const r = p.size * alpha;
            const ir = r * 0.4;
            if (si === 0) ctx.moveTo(Math.cos(angle) * r, Math.sin(angle) * r);
            else ctx.lineTo(Math.cos(angle) * r, Math.sin(angle) * r);
            const innerAngle = angle + Math.PI / 5;
            ctx.lineTo(Math.cos(innerAngle) * ir, Math.sin(innerAngle) * ir);
          }
          ctx.closePath(); ctx.fill();
          ctx.restore();
        } else {
          ctx.fillStyle = p.color;
          ctx.beginPath();
          ctx.arc(ox + p.x, oy + p.y, p.size * alpha, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;

      for (let sp of scorePopups) {
        const alpha = sp.life / sp.maxLife;
        ctx.globalAlpha = Math.max(0, alpha);
        ctx.save();
        ctx.translate(ox + sp.x, oy + sp.y);
        ctx.scale(1 + (1 - alpha) * 0.3, 1 + (1 - alpha) * 0.3);
        ctx.font = `bold ${sp.size}px sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillStyle = sp.color;
        ctx.shadowColor = 'rgba(0,0,0,0.8)';
        ctx.shadowBlur = 6;
        ctx.fillText(sp.text, 0, 0);
        ctx.shadowBlur = 0;
        ctx.restore();
      }
      ctx.globalAlpha = 1;
    }

    // ─────────────────────────────────────────────────────────────
    // DRAW: Trick display
    // ─────────────────────────────────────────────────────────────
    function drawTrickDisplay(ox: number, oy: number) {
      if (trickDisplayTimer <= 0 || !lastTrickName) return;
      const alpha = Math.min(1, trickDisplayTimer * 1.5);
      const scale = trickDisplayScale > 1 ? trickDisplayScale : 1;
      const x = ox + cw() * 0.5 + lateralPos * cw() * 0.22;
      const y = oy + ch() * 0.82 - jumpOffset - 120;
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(x, y);
      ctx.scale(scale, scale);
      ctx.shadowColor = '#ffd60a';
      ctx.shadowBlur = 30;
      ctx.fillStyle = '#ffd60a';
      ctx.font = 'bold 24px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(lastTrickName, 0, 0);
      if (lastTrickPoints > 0) {
        ctx.shadowBlur = 0;
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 16px sans-serif';
        ctx.fillText('+' + lastTrickPoints, 0, 24);
      }
      ctx.shadowBlur = 0;
      ctx.restore();
    }

    // ─────────────────────────────────────────────────────────────
    // DRAW: Combo meter
    // ─────────────────────────────────────────────────────────────
    function drawComboMeter(ox: number, oy: number) {
      if (comboMult <= 1 && comboWindowMs <= 0) return;
      const x = ox + cw() * 0.5;
      const y = oy + ch() * 0.92;
      const barW = cw() * 0.35;
      const barH = 10;
      const progress = comboWindowMs / COMBO_WINDOW_MS;
      ctx.save();
      if (comboMult > 1) {
        ctx.globalAlpha = 0.9;
        const badgeGrad = ctx.createLinearGradient(x - 40, y - 30, x + 40, y - 30);
        badgeGrad.addColorStop(0, '#ff6b00');
        badgeGrad.addColorStop(1, '#ffd60a');
        ctx.fillStyle = badgeGrad;
        ctx.beginPath(); ctx.roundRect(x - 44, y - 44, 88, 28, 8); ctx.fill();
        ctx.fillStyle = '#0d0d0f';
        ctx.font = 'bold 16px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('x' + comboMult.toFixed(1) + ' COMBO', x, y - 24);
        ctx.globalAlpha = 1;
      }
      if (comboWindowMs > 0) {
        ctx.fillStyle = 'rgba(0,0,0,0.4)';
        ctx.beginPath(); ctx.roundRect(x - barW / 2, y - 8, barW, barH, 5); ctx.fill();
        const barColor = progress > 0.5 ? '#00e5ff' : progress > 0.25 ? '#ffd60a' : '#ff4d6d';
        ctx.fillStyle = barColor;
        ctx.beginPath(); ctx.roundRect(x - barW / 2, y - 8, barW * progress, barH, 5); ctx.fill();
        if (progress < 0.3) {
          ctx.shadowColor = '#ff4d6d';
          ctx.shadowBlur = 12 * (1 - progress);
          ctx.strokeStyle = '#ff4d6d';
          ctx.lineWidth = 2;
          ctx.beginPath(); ctx.roundRect(x - barW / 2, y - 8, barW, barH, 5); ctx.stroke();
          ctx.shadowBlur = 0;
        }
      }
      ctx.restore();
    }

    // ─────────────────────────────────────────────────────────────
    // DRAW: BANKED TURN ARROW INDICATOR — big, urgent, impossible to miss
    // ─────────────────────────────────────────────────────────────
    function drawTurnArrow(ox: number, oy: number) {
      if (!arrowDirection || arrowUrgency <= 0) return;
      const w = cw();
      const h = ch();
      const isLeft = arrowDirection === 'left';
      const pulse = 0.6 + Math.sin(runTime * 8) * 0.4;
      const alpha = arrowUrgency * pulse;

      // Side panel arrow
      const arrowX = isLeft ? ox + w * 0.08 : ox + w * 0.92;
      const arrowY = oy + h * 0.55;
      const arrowSize = 60 + arrowUrgency * 40;

      ctx.save();
      ctx.globalAlpha = alpha;

      // Background glow
      ctx.shadowColor = arrowUrgency > 0.7 ? '#ff2200' : '#ffcc00';
      ctx.shadowBlur = 30 * arrowUrgency;

      // Arrow body
      ctx.fillStyle = arrowUrgency > 0.7 ? '#ff3300' : '#ffcc00';
      ctx.font = `bold ${Math.round(arrowSize)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(isLeft ? '◀◀' : '▶▶', arrowX, arrowY);

      // STEER text
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${Math.round(arrowSize * 0.35)}px sans-serif`;
      ctx.fillText('STEER!', arrowX, arrowY + arrowSize * 0.55);

      ctx.globalAlpha = 1;
      ctx.restore();

      // Edge danger flash — red vignette on the danger side
      if (arrowUrgency > 0.5) {
        const edgeAlpha = (arrowUrgency - 0.5) * 0.5 * pulse;
        const edgeGrad = ctx.createLinearGradient(
          isLeft ? ox : ox + w, oy,
          isLeft ? ox + w * 0.4 : ox + w * 0.6, oy
        );
        edgeGrad.addColorStop(0, `rgba(255,30,0,${edgeAlpha})`);
        edgeGrad.addColorStop(1, 'rgba(255,30,0,0)');
        ctx.fillStyle = edgeGrad;
        ctx.fillRect(ox, oy, w, h);
      }
    }

    // ─────────────────────────────────────────────────────────────
    // DRAW: Level progress bar
    // ─────────────────────────────────────────────────────────────
    function drawLevelProgress(ox: number, oy: number) {
      const cfg = getLevelConfig();
      const progress = Math.min(1, runTime / cfg.duration);
      const w = cw();
      const barW = w * 0.55;
      const barH = 8;
      const barX = ox + w * 0.5 - barW * 0.5;
      const barY = oy + 14;
      ctx.fillStyle = 'rgba(0,0,0,0.45)';
      ctx.beginPath(); ctx.roundRect(barX - 2, barY - 2, barW + 4, barH + 4, 6); ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx.beginPath(); ctx.roundRect(barX, barY, barW, barH, 4); ctx.fill();
      const r = Math.round(255 * Math.min(1, progress * 2));
      const g = Math.round(255 * Math.min(1, (1 - progress) * 2));
      ctx.fillStyle = `rgb(${r},${g},60)`;
      ctx.beginPath(); ctx.roundRect(barX, barY, barW * progress, barH, 4); ctx.fill();
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('🏁', barX + barW + 6, barY + barH);
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.font = 'bold 11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`LVL ${cfg.level} — ${cfg.name}`, ox + w * 0.5, barY - 4);
    }

    // ─────────────────────────────────────────────────────────────
    // DRAW: Rider
    // ─────────────────────────────────────────────────────────────
    function drawRider(ox: number, oy: number, t: number) {
      const w = cw();
      const h = ch();
      // Player stays centered horizontally; slight lateral drift visible but subtle
      // Ground position lower on screen = more immersive first-person-adjacent feel
      const baseX = ox + w * 0.5 + lateralPos * w * 0.22 + trackCurveOffset * w * 0.04;
      const groundY = oy + h * 0.84;
      const riderY = groundY - jumpOffset;
      const leanAngle = lateralVel * 0.8;

      if (isCrashed) { drawCrashedRider(baseX, riderY, t, ox, oy); return; }

      ctx.save();
      ctx.translate(baseX, riderY);
      ctx.rotate(leanAngle * 0.04);

      if (!airborne) {
        ctx.fillStyle = 'rgba(0,0,0,0.28)';
        ctx.beginPath(); ctx.ellipse(0, 16, 42, 7, 0, 0, Math.PI * 2); ctx.fill();
      } else {
        const shadowAlpha = Math.max(0, 0.18 - jumpOffset * 0.0015);
        ctx.fillStyle = 'rgba(0,0,0,' + shadowAlpha + ')';
        ctx.beginPath(); ctx.ellipse(0, groundY - riderY + 16, 36, 6, 0, 0, Math.PI * 2); ctx.fill();
      }

      const flipScaleY = boardFlipActive ? Math.cos((boardFlipAngle * Math.PI) / 180) : 1;
      const boardW = 76;
      const boardH = 13;
      const scaledH = Math.max(2, boardH * Math.abs(flipScaleY));
      const boardY = 6;

      const deckGrad = ctx.createLinearGradient(-boardW / 2, boardY, boardW / 2, boardY + scaledH);
      deckGrad.addColorStop(0, flipScaleY < 0 ? '#7a4a18' : '#d4982e');
      deckGrad.addColorStop(0.4, flipScaleY < 0 ? '#5a3010' : '#b87828');
      deckGrad.addColorStop(1, flipScaleY < 0 ? '#7a4a18' : '#d4982e');
      ctx.fillStyle = deckGrad;
      ctx.beginPath(); ctx.roundRect(-boardW / 2, boardY, boardW, scaledH, 6); ctx.fill();
      ctx.fillStyle = flipScaleY < 0 ? '#6a3a10' : '#c88020';
      ctx.beginPath(); ctx.roundRect(-boardW / 2, boardY, 10, scaledH, [6, 0, 0, 6]); ctx.fill();
      ctx.beginPath(); ctx.roundRect(boardW / 2 - 10, boardY, 10, scaledH, [0, 6, 6, 0]); ctx.fill();
      ctx.fillStyle = '#b0bec5';
      ctx.fillRect(-boardW / 2 + 4, boardY + scaledH, boardW - 8, 5);

      const wheelY = boardY + scaledH + 9;
      const wheelXs = [-26, 26];
      for (const wx of wheelXs) {
        ctx.fillStyle = '#e0c060';
        ctx.beginPath(); ctx.arc(wx, wheelY, 9, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#8a6820';
        ctx.beginPath(); ctx.arc(wx, wheelY, 4, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#c0c0c0';
        ctx.beginPath(); ctx.arc(wx, wheelY, 2, 0, Math.PI * 2); ctx.fill();
        if (!airborne && speed > 2) {
          const sa = t * speed * 5;
          ctx.strokeStyle = 'rgba(200,160,60,0.75)'; ctx.lineWidth = 1.5;
          for (let sp = 0; sp < 3; sp++) {
            const ang = sa + (sp * Math.PI * 2) / 3;
            ctx.beginPath();
            ctx.moveTo(wx + Math.cos(ang) * 2, wheelY + Math.sin(ang) * 2);
            ctx.lineTo(wx + Math.cos(ang) * 7.5, wheelY + Math.sin(ang) * 7.5);
            ctx.stroke();
          }
        }
      }

      const charH = 160;
      const charW = 80;
      const charTop = boardY - charH;

      ctx.save();
      if (spinActive) { ctx.rotate((spinDeg * Math.PI) / 180); }

      if (charLoaded && charImg.complete && charImg.naturalWidth > 0) {
        const gifAspect = charImg.naturalWidth / charImg.naturalHeight;
        const gifH = charH * 1.5;
        const gifW = gifH * gifAspect;
        ctx.drawImage(charImg, -gifW / 2, charTop - gifH * 0.05, gifW, gifH);
      } else {
        drawHeroGirl(t, charTop, charW, charH);
      }
      ctx.restore();

      if (airborne && airTime > 0.2) {
        const glowAlpha = Math.min(0.7, (airTime - 0.2) * 0.9);
        const glowColor = airTime > 1.0 ? '#ff9f43' : '#ffd60a';
        ctx.shadowColor = glowColor;
        ctx.shadowBlur = 36;
        ctx.strokeStyle = 'rgba(255,214,10,' + glowAlpha + ')';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(0, charTop + charH * 0.38, 62 + Math.sin(t * 7) * 8, 0, Math.PI * 2);
        ctx.stroke();
        ctx.shadowBlur = 0;
        if (airTime > 0.8) {
          ctx.save();
          ctx.globalAlpha = Math.min(1, (airTime - 0.8) * 2);
          ctx.fillStyle = '#ff9f43';
          ctx.font = 'bold 18px sans-serif';
          ctx.textAlign = 'center';
          ctx.shadowColor = '#ff6b00';
          ctx.shadowBlur = 12;
          ctx.fillText('BIG AIR!', 0, charTop - 20);
          ctx.shadowBlur = 0;
          ctx.restore();
        }
      }

      if (speed > MAX_SPEED * 0.45) {
        const lineAlpha = ((speed - MAX_SPEED * 0.45) / (MAX_SPEED * 0.55)) * 0.38;
        ctx.strokeStyle = 'rgba(200,230,255,' + lineAlpha + ')';
        ctx.lineWidth = 1.5;
        for (let li = 0; li < 6; li++) {
          const ly = charTop + 15 + li * 24;
          const len = 22 + li * 14;
          ctx.beginPath(); ctx.moveTo(-charW / 2 - 12 - len, ly); ctx.lineTo(-charW / 2 - 12, ly); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(charW / 2 + 12, ly); ctx.lineTo(charW / 2 + 12 + len, ly); ctx.stroke();
        }
      }

      if (grabActive) {
        ctx.strokeStyle = '#d4956a'; ctx.lineWidth = 8; ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(-20, charTop + charH * 0.55);
        ctx.bezierCurveTo(-30, charTop + charH * 0.72, -28, charTop + charH * 0.85, -boardW / 2 + 10, boardY + 4);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(20, charTop + charH * 0.55);
        ctx.bezierCurveTo(30, charTop + charH * 0.72, 28, charTop + charH * 0.85, boardW / 2 - 10, boardY + 4);
        ctx.stroke();
        ctx.lineCap = 'butt';
      }

      ctx.restore();
      void h;
    }

    // ─────────────────────────────────────────────────────────────
    // DRAW: Hero Girl fallback
    // ─────────────────────────────────────────────────────────────
    function drawHeroGirl(t: number, charTop: number, charW: number, charH: number) {
      hairWave += 0.065;
      const footY = charTop + charH;
      const crouchY = airborne ? -14 : 0;
      const legTopY = footY - 64 + crouchY;
      const legGrad = ctx.createLinearGradient(-20, legTopY, -8, footY);
      legGrad.addColorStop(0, '#d4956a');
      legGrad.addColorStop(1, '#b87045');
      ctx.fillStyle = legGrad;
      ctx.beginPath();
      ctx.moveTo(-24, legTopY);
      ctx.bezierCurveTo(-26, legTopY + 18, -24, footY - 10, -24, footY - 2);
      ctx.lineTo(-10, footY - 2);
      ctx.bezierCurveTo(-10, footY - 10, -16, legTopY + 20, -14, legTopY);
      ctx.closePath(); ctx.fill();
      ctx.beginPath();
      ctx.moveTo(10, legTopY);
      ctx.bezierCurveTo(8, legTopY + 18, 10, footY - 10, 10, footY - 2);
      ctx.lineTo(24, footY - 2);
      ctx.bezierCurveTo(24, footY - 10, 26, legTopY + 20, 24, legTopY);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#1a1a2e';
      ctx.beginPath();
      ctx.moveTo(-26, footY - 4);
      ctx.bezierCurveTo(-22, footY - 4, -18, footY - 4, -14, footY - 4);
      ctx.lineTo(14, footY - 4);
      ctx.bezierCurveTo(18, footY - 4, 24, footY - 4, 26, footY - 4);
      ctx.closePath(); ctx.fill();
      const hipY = footY - 64 + crouchY;
      ctx.fillStyle = '#1a1a2e';
      ctx.beginPath();
      ctx.moveTo(-26, hipY + 24);
      ctx.bezierCurveTo(-24, hipY - 8, -18, hipY + 18, -14, hipY + 6);
      ctx.lineTo(14, hipY + 6);
      ctx.bezierCurveTo(18, hipY + 18, 24, hipY - 8, 26, hipY + 24);
      ctx.closePath(); ctx.fill();
      const torsoY = hipY - 40;
      ctx.fillStyle = '#ff6b9d';
      ctx.beginPath();
      ctx.moveTo(-16, hipY + 2);
      ctx.bezierCurveTo(-18, hipY - 8, -14, torsoY + 22, -12, torsoY + 8);
      ctx.lineTo(12, torsoY + 8);
      ctx.bezierCurveTo(14, torsoY + 22, 18, hipY - 8, 16, hipY + 2);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#d4956a';
      ctx.beginPath(); ctx.roundRect(-6, torsoY - 16, 12, 18, 4); ctx.fill();
      const headY = torsoY - 36;
      ctx.fillStyle = '#f0c090';
      ctx.beginPath(); ctx.ellipse(0, headY, 18, 20, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#2a1a0a';
      ctx.beginPath(); ctx.ellipse(-7, headY - 4, 4, 5, 0, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.ellipse(7, headY - 4, 4, 5, 0, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = 'white';
      ctx.beginPath(); ctx.ellipse(-8, headY - 5, 2, 2.5, 0, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.ellipse(6, headY - 5, 2, 2.5, 0, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#8a4020'; ctx.lineWidth = 2; ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(-7, headY + 8);
      ctx.bezierCurveTo(-4, headY + 13, 4, headY + 13, 7, headY + 8);
      ctx.stroke(); ctx.lineCap = 'butt';
      const hairCapGrad = ctx.createRadialGradient(-2, headY - 8, 0, 0, headY, 26);
      hairCapGrad.addColorStop(0, '#fff8c0');
      hairCapGrad.addColorStop(0.4, '#f8e060');
      hairCapGrad.addColorStop(1, '#c89010');
      ctx.fillStyle = hairCapGrad;
      ctx.beginPath(); ctx.ellipse(0, headY - 2, 22, 20, 0, 0, Math.PI * 2); ctx.fill();
      const windX = -speed * 1.4;
      ctx.fillStyle = '#f0d050';
      ctx.beginPath();
      ctx.moveTo(-10, headY + 6);
      ctx.bezierCurveTo(-14 + windX * 0.12, headY + 22, -10 + windX * 0.28, headY + 46, -6 + windX * 0.48, headY + 72 + Math.sin(hairWave) * 8);
      ctx.bezierCurveTo(windX * 0.52, headY + 82, 8 + windX * 0.58, headY + 84, 12 + windX * 0.52, headY + 76);
      ctx.bezierCurveTo(8 + windX * 0.38, headY + 60, 6 + windX * 0.22, headY + 38, 10, headY + 16);
      ctx.bezierCurveTo(6, headY + 8, 0, headY + 4, -10, headY + 6);
      ctx.fill();
      ctx.fillStyle = '#ff6b9d';
      ctx.beginPath(); ctx.ellipse(-2, headY + 10, 5, 4, 0.3, 0, Math.PI * 2); ctx.fill();
      void t; void charW;
    }

    function drawCrashedRider(x: number, y: number, t: number, ox: number, oy: number) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(Math.sin(t * 2) * 0.3 - 0.5);
      ctx.fillStyle = '#d4956a';
      ctx.beginPath(); ctx.roundRect(-32, -16, 64, 28, 8); ctx.fill();
      ctx.strokeStyle = '#1a1a1a'; ctx.lineWidth = 3;
      ctx.beginPath(); ctx.moveTo(-8, -8); ctx.bezierCurveTo(-4, 0, 4, -4, 8, 4); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(8, -8); ctx.bezierCurveTo(4, 0, -4, -4, -8, 4); ctx.stroke();
      ctx.fillStyle = '#1a1a2e';
      ctx.beginPath(); ctx.roundRect(-18, 10, 36, 16, 4); ctx.fill();
      ctx.restore();
      ctx.save();
      ctx.translate(x - 42, y - 18);
      ctx.rotate(-0.4);
      ctx.fillStyle = '#f0c090';
      ctx.beginPath(); ctx.ellipse(0, 0, 16, 18, 0, 0, Math.PI * 2); ctx.fill();
      const hairGrad = ctx.createRadialGradient(-2, -8, 0, 0, -4, 26);
      hairGrad.addColorStop(0, '#fff5b0');
      hairGrad.addColorStop(1, '#d4a820');
      ctx.fillStyle = hairGrad;
      ctx.beginPath(); ctx.ellipse(0, -8, 22, 18, 0, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
      ctx.save();
      ctx.translate(x + 40, y - 26);
      ctx.rotate(t * 3.5 + 0.8);
      const bGrad = ctx.createLinearGradient(-32, -6, 32, 6);
      bGrad.addColorStop(0, '#c8902a'); bGrad.addColorStop(1, '#8a6020');
      ctx.fillStyle = bGrad;
      ctx.beginPath(); ctx.roundRect(-32, -6, 64, 12, 6); ctx.fill();
      ctx.fillStyle = '#d4a840';
      ctx.beginPath(); ctx.arc(-22, 10, 7, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(22, 10, 7, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
      for (let si = 0; si < 5; si++) {
        const angle = (si / 5) * Math.PI * 2 + t * 2.5;
        ctx.fillStyle = si % 2 === 0 ? '#ffd60a' : '#ffffff';
        ctx.beginPath();
        ctx.arc(x - 42 + Math.cos(angle) * 28, y - 30 + Math.sin(angle) * 20, 4, 0, Math.PI * 2);
        ctx.fill();
      }
      void ox; void oy;
    }

    // ─────────────────────────────────────────────────────────────
    // GAME LOOP
    // ─────────────────────────────────────────────────────────────
    let lastTime = performance.now();

    function loop(now: number) {
      rafRef.current = requestAnimationFrame(loop);
      const rawDelta = Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;

      const gs = gameStateRef.current;
      // Only pause for actual pauses/permission — combo video (cutscene) no longer blocks
      if (gs === 'paused' || gs === 'permission') return;
      // Allow game to keep running during cutscene state (combo video overlay)
      // Only hard-stop for area_select and level_complete
      if (gs === 'area_select') return;

      const w = cw();
      const h = ch();
      const cfg = getLevelConfig();

      if (!isCrashed && gs === 'running' && !levelDone) {
        const inSafeZone = runTime < SAFE_ZONE_SECONDS;
        const progressFraction = Math.min(1, runTime / cfg.duration);

        // Speed ramp — eased curve for more exciting acceleration feel
        if (inSafeZone) {
          speed = BASE_SPEED * 0.7;
        } else {
          const postSafe = (runTime - SAFE_ZONE_SECONDS) / (cfg.duration - SAFE_ZONE_SECONDS);
          // Ease-in-out curve: slow start, fast middle, slight plateau at end
          const easedProgress = postSafe < 0.5
            ? 2 * postSafe * postSafe
            : 1 - Math.pow(-2 * postSafe + 2, 2) / 2;
          const baseTarget = BASE_SPEED + easedProgress * (MAX_SPEED - BASE_SPEED) * cfg.speedMultiplier;
          speed = Math.min(MAX_SPEED, baseTarget + momentumBoost);
        }

        momentumBoost = Math.max(0, momentumBoost - rawDelta * 0.8);
        if (crashCooldown > 0) crashCooldown -= rawDelta;
        if (comboVideoCooldown > 0) comboVideoCooldown -= rawDelta;

        if (!inSafeZone) updateProgressiveDifficulty(runTime - SAFE_ZONE_SECONDS);

        // ─────────────────────────────────────────────────────────
        // EXTREME BANKED TURN SYSTEM
        // ─────────────────────────────────────────────────────────
        trackCurveTimer += rawDelta;

        // Regenerate turn queue when exhausted
        if (turnQueue.length === 0 || currentTurnIndex >= turnQueue.length) {
          turnQueue = generateTurnSequence(progressFraction);
          currentTurnIndex = 0;
        }

        const currentTurn = turnQueue[currentTurnIndex];
        if (currentTurn) {
          // How long this turn segment lasts (shorter when going fast)
          const turnDuration = inSafeZone
            ? currentTurn.duration * 2.2
            : currentTurn.duration * (1.0 - progressFraction * 0.30);

          if (trackCurveTimer >= turnDuration) {
            trackCurveTimer = 0;
            currentTurnIndex++;
          }

          // Snap to target with urgency — faster at higher speeds
          const speedFactor = 1.0 + (speed / MAX_SPEED) * 0.6;
          const snapSpeed = inSafeZone ? 0.4 : (currentTurn.urgency * (1.4 + progressFraction * 1.2) * speedFactor);
          trackCurveTarget = inSafeZone ? currentTurn.target * 0.35 : currentTurn.target;
          trackCurveOffset += (trackCurveTarget - trackCurveOffset) * rawDelta * snapSpeed;
          trackCurveOffset = Math.max(-1.0, Math.min(1.0, trackCurveOffset));

          // Bank angle — visual tilt of track — more dramatic (32 degrees max)
          bankAngleTarget = trackCurveOffset * 32;
          bankAngle += (bankAngleTarget - bankAngle) * rawDelta * 5;

          // Curve pressure — how hard the turn is pushing the player off
          curvePressure = inSafeZone ? 0 : Math.min(1, Math.abs(trackCurveOffset) / 0.55);

          // Update obstacle hint for intelligent placement
          currentObstacleHint = currentTurn.obstacleHint;

          // Arrow direction indicator
          if (Math.abs(trackCurveOffset) > 0.10 && !inSafeZone) {
            arrowDirection = trackCurveOffset > 0 ? 'right' : 'left';
            arrowUrgency = Math.min(1, Math.abs(trackCurveOffset) / 0.7);
          } else {
            arrowDirection = null;
            arrowUrgency = Math.max(0, arrowUrgency - rawDelta * 3);
          }
        }

        // ── BANKED TURN PHYSICS: curve pushes player toward the outside
        // Forgiving centrifugal force — player drifts but never instantly crashes from steering
        if (!inSafeZone && Math.abs(trackCurveOffset) > 0.08) {
          // Reduced centrifugal force — steering helps but missing a turn won't instantly kill you
          const speedFactor = 0.7 + (speed / MAX_SPEED) * 0.6;
          const centrifugalForce = trackCurveOffset * curvePressure * 0.045 * speedFactor * (1 + progressFraction * 0.5);
          lateralVel += centrifugalForce * rawDelta * 60;
        }

        // ── Lateral steering — very responsive
        const tiltSensitivity = inSafeZone ? 0.028 : 0.085;
        const keys = (window as unknown as Record<string, unknown>).__surfsterre_keys as { current: Set<string> } | undefined;
        if (keys?.current) {
          const leftHeld = keys.current.has('ArrowLeft');
          const rightHeld = keys.current.has('ArrowRight');
          if (leftHeld && !rightHeld) tiltAngle = -48;
          else if (rightHeld && !leftHeld) tiltAngle = 48;
          else if (!leftHeld && !rightHeld && tiltAngle !== 0 && controlModeRef.current !== 'tilt') tiltAngle = 0;
        }
        const tiltInput = Math.max(-50, Math.min(50, tiltAngle));
        lateralVel += tiltInput * tiltSensitivity * rawDelta * 60 * 0.06;
        lateralVel *= 0.80; // snappy friction
        lateralPos += lateralVel * rawDelta * 60;

        // Boundary — forgiving: soft bounce well before crash zone, only crash at extreme edge
        const boundary = inSafeZone ? 0.98 : (0.92 - progressFraction * 0.05);
        const crashBoundary = 1.18; // much wider — only crash if truly off the road
        if (Math.abs(lateralPos) > boundary) {
          if (!inSafeZone && Math.abs(lateralPos) > crashBoundary) {
            triggerCrash();
          } else {
            // Soft bounce — push back toward center, no crash
            lateralPos = Math.sign(lateralPos) * boundary * 0.92;
            lateralVel *= -0.45;
          }
        }
        lateralPos = Math.max(-1.25, Math.min(1.25, lateralPos));

        runTime += rawDelta;
        distance += speed * rawDelta * 60 * 0.02;

        const postSafeFrac = inSafeZone ? 0 : Math.min(1, (runTime - SAFE_ZONE_SECONDS) / 40);
        // Faster depth speed = obstacles rush toward player more aggressively (real-world skating feel)
        const depthSpeed = speed * rawDelta * (0.035 + postSafeFrac * 0.032);

        // Finish line
        const timeLeft = cfg.duration - runTime;
        if (timeLeft <= cfg.duration * 0.35 && !finishLineCrossed) {
          finishLineVisible = true;
          const finishProgress = 1 - (timeLeft / (cfg.duration * 0.35));
          finishLineDepth = 0.05 + finishProgress * 1.1;
        }
        if (finishLineVisible && finishLineDepth >= 0.95 && !finishLineCrossed) {
          finishLineCrossed = true;
          spawnStarBurst(w * 0.5, h * 0.6, true);
          spawnStarBurst(w * 0.3, h * 0.7, true);
          spawnStarBurst(w * 0.7, h * 0.7, true);
          screenFlash('#ffd60a', 0.5);
          haptic(100);
        }

        // Update obstacles
        for (const o of obstacles) {
          if (!o.active) continue;
          o.depth += depthSpeed;
          // Update moving obstacle position (cows wander)
          if (o.type === 0) {
            o.laneX = Math.sin(o.movePhase + runTime * o.moveSpeed * 60) * 0.45;
          }
          if (o.depth > 1.15) {
            // Check: never place obstacle directly in front of a ramp
            // Find the nearest ramp that is about to come into view
            let nearestRampLaneX: number | null = null;
            let nearestRampDepth = -999;
            for (const r of ramps) {
              if (r.active && r.depth < -0.05 && r.depth > nearestRampDepth) {
                nearestRampDepth = r.depth;
                nearestRampLaneX = r.laneX;
              }
            }
            const baseRecycle = -(cfg.obstacleRecycleMin + Math.random() * (cfg.obstacleRecycleMax - cfg.obstacleRecycleMin));
            const tooCloseToRamp = nearestRampLaneX !== null && Math.abs(baseRecycle - nearestRampDepth) < 0.18;
            o.depth = tooCloseToRamp ? baseRecycle - 0.22 : baseRecycle;
            const isFullWidth = Math.random() < cfg.fullWidthChance;
            const isGap = Math.random() < cfg.gapChance;
            if (isFullWidth || isGap) {
              o.laneX = 0;
            } else {
              // Smart lane — but if too close to ramp lane, offset
              let candidateLane = getSmartLaneX(currentObstacleHint, false);
              if (nearestRampLaneX !== null && tooCloseToRamp && Math.abs(candidateLane - nearestRampLaneX) < 0.2) {
                // Push obstacle to opposite side of ramp
                candidateLane = nearestRampLaneX > 0 ? -0.55 : 0.55;
              }
              o.laneX = candidateLane;
            }
            // Pick obstacle types based on area and level
            // Types 0-7: Italian  |  Types 8-9: Japan (rice bowl=8, deer=9)  |  Types 10-11: Rainbow Road (mushroom=10, star=11)
            if (isGap) {
              o.type = 3;
            } else if (currentLevelRef.current === 3 || areaId === 'rainbow_road') {
              // Rainbow Road — mushrooms and stars
              o.type = [10, 11][Math.floor(Math.random() * 2)];
            } else if (areaId === 'japan') {
              // Japan Mt Fuji — rice bowls and deer
              o.type = [8, 9][Math.floor(Math.random() * 2)];
            } else {
              // Italy — classic Italian obstacles
              o.type = [0, 1, 2, 4, 5, 6, 7][Math.floor(Math.random() * 7)];
            }
            o.animPhase = Math.random() * Math.PI * 2;
            o.fullWidth = isFullWidth || isGap;
            o.warned = false;
            o.moveDir = Math.random() < 0.5 ? 1 : -1;
            o.moveSpeed = 0.003 + Math.random() * 0.005;
            o.movePhase = Math.random() * Math.PI * 2;
          }
        }
        for (const r of ramps) {
          if (!r.active) continue;
          r.depth += depthSpeed;
          r.glowPhase += rawDelta * 3;
          if (r.depth > 1.15) {
            // Find nearest obstacle to avoid placing ramp directly behind one
            let minObsDepth = -999;
            for (const o of obstacles) {
              if (o.active && o.depth < 0 && o.depth > minObsDepth) minObsDepth = o.depth;
            }
            const baseRecycle = -(cfg.obstacleRecycleMin * 1.2 + Math.random() * 0.35);
            const tooClose = Math.abs(baseRecycle - minObsDepth) < 0.18;
            r.depth = tooClose ? baseRecycle - 0.22 : baseRecycle;
            // Smart ramp placement — ramps on outside of turns reward good steering
            r.laneX = getSmartLaneX(currentObstacleHint, true);
            r.rampType = Math.floor(Math.random() * 6);
            r.used = false;
          }
        }

        // Spawn debug
        {
          const activeObs = obstacles.filter(o => o.active);
          const activeRmp = ramps.filter(r => r.active);
          let closestObsDepth: number | null = null;
          for (const o of activeObs) {
            if (closestObsDepth === null || o.depth > closestObsDepth) closestObsDepth = o.depth;
          }
          let closestRampDepth: number | null = null;
          for (const r of activeRmp) {
            if (closestRampDepth === null || r.depth > closestRampDepth) closestRampDepth = r.depth;
          }
          pushSpawnDebug({
            depthSpeed: depthSpeed / rawDelta,
            activeObstacles: activeObs.length,
            activeRamps: activeRmp.length,
            closestObstacleDepth: closestObsDepth,
            closestRampDepth: closestRampDepth,
            spawnMinDepth: -cfg.obstacleRecycleMin,
            spawnMaxDepth: -(cfg.obstacleRecycleMin + (cfg.obstacleRecycleMax - cfg.obstacleRecycleMin)),
            runTime,
            postSafeFrac,
          });
        }

        // Jump buffer
        if (jumpBufferTimer > 0) {
          jumpBufferTimer -= rawDelta;
          if (jumpBufferTimer <= 0) jumpBuffered = false;
        }

        // Coyote time
        if (!airborne && jumpOffset <= 0) {
          coyoteTime = 0.12;
        } else if (coyoteTime > 0) {
          coyoteTime -= rawDelta;
        }

        // Buffered jump
        if (jumpBuffered && !airborne) {
          jumpBuffered = false;
          jumpBufferTimer = 0;
          jumpVel = JUMP_VY;
          airborne = true;
          jumpCount = 0;
          coyoteTime = 0;
          trickActiveDuringLanding = false;
          comboSequence.push({ type: 'jump', time: runTime });
          spawnParticles(w * 0.5 + lateralPos * w * 0.22, h * 0.84, '#a8e6cf', 14, false);
          haptic(12);
        }

        // Jump physics
        if (airborne || jumpOffset > 0) {
          jumpVel += GRAVITY;
          jumpOffset -= jumpVel;
          if (jumpOffset <= 0) {
            if (airborne) {
              perfectLanding = !trickActiveDuringLanding;
              if (trickActiveDuringLanding && boardFlipActive && !spinActive && !inSafeZone && airTime > 0.3) {
                triggerCrash();
              } else {
                jumpOffset = 0; jumpVel = 0; airborne = false; jumpCount = 0;
                trickActiveDuringLanding = false; boardFlipActive = false; spinActive = false;
                comboSequence.push({ type: 'jump', time: runTime });
                spawnParticles(w * 0.5 + lateralPos * w * 0.26, h * 0.80, '#c0e0ff', 18, false);
                shakeIntensity = 3.5; haptic(15);
                if (comboCount > 0) {
                  comboMult = Math.min(8, comboCount + 1);
                  comboWindowMs = COMBO_WINDOW_MS;
                }
                if (perfectLanding && comboCount > 0) {
                  score += 50;
                  spawnScorePopup(w * 0.5 + lateralPos * w * 0.22, h * 0.75, 'PERFECT!', '#00e5ff', false);
                }
                onTrickLandedRef.current('', 0);
              }
            } else {
              jumpOffset = 0; jumpVel = 0;
            }
          } else {
            airborne = true;
          }
        }

        airTime = airborne ? airTime + rawDelta : 0;

        // Spin animation
        if (spinActive) {
          spinDeg += 600 * rawDelta;
          if (spinDeg >= spinTarget) {
            spinActive = false; spinDeg = 0; trickActiveDuringLanding = false;
            awardTrick('360 Spin', 140);
          }
        }

        // Board flip animation
        if (boardFlipActive) {
          boardFlipAngle += 720 * rawDelta;
          if (boardFlipAngle >= 360) { boardFlipAngle = 0; boardFlipActive = false; trickActiveDuringLanding = false; }
        }

        // Grab timer
        if (grabTimer > 0) {
          grabTimer -= rawDelta;
          if (grabTimer <= 0) { grabActive = false; grabTimer = 0; }
        }

        // Combo window
        if (comboWindowMs > 0) {
          comboWindowMs -= rawDelta * 1000;
          if (comboWindowMs <= 0) {
            comboWindowMs = 0; comboCount = 0; comboMult = 1; comboStreak = 0;
          }
        }

        // Trick display timer
        if (trickDisplayTimer > 0) {
          trickDisplayTimer -= rawDelta;
          trickDisplayScale = Math.max(1, trickDisplayScale - rawDelta * 3);
          if (trickDisplayTimer <= 0) { trickDisplayTimer = 0; lastTrickName = ''; lastTrickPoints = 0; }
        }

        // Ramp collision — auto-launch
        if (!inSafeZone) {
          for (const r of ramps) {
            if (!r.active || r.used) continue;
            if (r.depth > 0.88 && r.depth < 1.02 && !airborne) {
              const laneDiff = Math.abs(lateralPos - r.laneX);
              const rampHalfWidth = r.rampType >= 1 ? 0.45 : 0.32;
              if (laneDiff <= rampHalfWidth) {
                r.used = true;
                // Ramp type determines launch power
                const launchPowers = [1.15, 1.65, 1.35, 1.45, 1.75, 1.50];
                jumpVel = JUMP_VY * (launchPowers[r.rampType] ?? 1.2);
                airborne = true;
                jumpCount = 0;
                trickActiveDuringLanding = false;
                comboSequence.push({ type: 'jump', time: runTime });
                checkComboVideoTrigger('B');
                const rampColors = ['#ffd60a', '#ff9f43', '#00e5ff', '#c084fc', '#ff4400', '#ffd60a'];
                spawnParticles(w * 0.5 + lateralPos * w * 0.22, h * 0.84, rampColors[r.rampType], 28, false);
                spawnStarBurst(w * 0.5 + lateralPos * w * 0.22, h * 0.84, r.rampType >= 1);
                if (r.rampType >= 1) spawnFireBurst(w * 0.5 + lateralPos * w * 0.22, h * 0.84);
                screenFlash(rampColors[r.rampType], r.rampType >= 1 ? 0.30 : 0.18);
                haptic(r.rampType >= 1 ? 45 : 18);
                break;
              }
            }
          }
        }

        // Obstacle collision
        if (!inSafeZone) {
          for (const o of obstacles) {
            if (!o.active) continue;
            if (o.depth > 0.86 && o.depth < 1.04) {
              if (o.type === 3) {
                if (!airborne) {
                  const laneDiff = Math.abs(lateralPos - o.laneX);
                  if (laneDiff < 0.52) { triggerCrash(); break; }
                }
              } else if (!airborne) {
                const hitWidth = o.fullWidth ? 0.46 : 0.22;
                const laneDiff = Math.abs(lateralPos - o.laneX);
                if (laneDiff < hitWidth) { triggerCrash(); break; }
              }
            }
          }
        }

        // Camera shake
        if (shakeIntensity > 0) {
          shakeX = (Math.random() - 0.5) * shakeIntensity;
          shakeY = (Math.random() - 0.5) * shakeIntensity;
          shakeIntensity *= 0.76;
          if (shakeIntensity < 0.1) { shakeX = 0; shakeY = 0; shakeIntensity = 0; }
        }

        // Particles
        const decay = rawDelta * 2.5;
        particles = particles.filter(p => {
          p.x += p.vx; p.y += p.vy; p.vy += 0.12; p.life -= decay;
          return p.life > 0;
        });

        // Score popups
        scorePopups = scorePopups.filter(sp => {
          sp.y += sp.vy;
          sp.vy *= 0.95;
          sp.life -= rawDelta;
          return sp.life > 0;
        });

        // HUD update
        hudTimer += rawDelta;
        if (hudTimer > 0.1) {
          hudTimer = 0;
          onHUDUpdateRef.current({
            runTimeSeconds: runTime,
            speed: Math.round((speed / MAX_SPEED) * 100),
            distance: Math.round(distance),
            isAirborne: airborne,
            airTime,
            health,
            score,
            comboMultiplier: comboMult,
            comboCount,
            comboWindowActive: comboWindowMs > 0,
            comboWindowProgress: comboWindowMs > 0 ? comboWindowMs / COMBO_WINDOW_MS : 0,
          });
        }

        // Level end
        if (!levelDone && (finishLineCrossed || runTime >= cfg.duration)) {
          levelDone = true;
          const key = `surfsterre_best_${areaId}_lvl${currentLevelRef.current}`;
          const prev = parseInt(localStorage.getItem(key) || '0', 10);
          if (score > prev) localStorage.setItem(key, String(score));
          pushGameLog({ type: 'system', message: 'Level ' + cfg.level + ' complete! Score: ' + score });
          onLevelCompleteRef.current(cfg.level);
        }

        // Warm-up jumps
        if (warmupJumpsRemaining > 0 && runTime > 0.3) {
          warmupJumpTimer += rawDelta;
          if (warmupJumpTimer >= WARMUP_JUMP_INTERVAL && !airborne) {
            warmupJumpTimer = 0;
            warmupJumpsRemaining--;
            jumpVel = JUMP_VY * 0.9;
            airborne = true;
            jumpCount = 0;
            trickActiveDuringLanding = false;
            spawnParticles(w * 0.5 + lateralPos * w * 0.22, h * 0.84, '#a8e6cf', 14, false);
            spawnSpeedSparks(w * 0.5 + lateralPos * w * 0.22, h * 0.84);
            haptic(12);
          }
        }

        void h;
      }

      // Render
      ctx.clearRect(0, 0, w, h);
      drawBackground(shakeX, shakeY);
      drawSlope(shakeX, shakeY);
      drawFinishLine(shakeX, shakeY);
      drawRamps(shakeX, shakeY);
      drawObstacles(shakeX, shakeY);
      drawParticles(shakeX, shakeY);
      drawRider(shakeX, shakeY, runTime);
      drawTrickDisplay(shakeX, shakeY);
      drawComboMeter(shakeX, shakeY);
      drawLevelProgress(shakeX, shakeY);
      drawTurnArrow(shakeX, shakeY);

      void h;
    }

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('surfsterre_jump', onJump);
      window.removeEventListener('surfsterre_spin', onSpin);
      window.removeEventListener('surfsterre_trick', onTrick);
      window.removeEventListener('surfsterre_combo', onComboBtn);
      window.removeEventListener('surfsterre_tilt', onTiltEvt);
      window.removeEventListener('surfsterre_restart', onRestart);
      window.removeEventListener('surfsterre_combo_video_end', onComboVideoEnd);
      window.removeEventListener('surfsterre_outfit_change', onOutfitChange);
      if (controlModeRef.current === 'tilt') {
        window.removeEventListener('deviceorientation', onDeviceOrientation);
      }
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      delete (window as unknown as Record<string, unknown>).__surfsterre_keys;
      if (charImgRef.current && charImgRef.current.parentNode) {
        charImgRef.current.parentNode.removeChild(charImgRef.current);
        charImgRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ touchAction: 'none', display: 'block' }}
    />
  );
}